/**
 * Brand assets page and brand editor component translations
 */
export const brandAssets = {
  en: {
    // Brand assets page
    loadingBrandAssets: "Loading brand assets",
    buildWithAi: "Build with AI",
    manageBrandGuidelines: "Manage your brand identity across {total} guideline sections",
    brandGuidelines: "Brand Guidelines",
    percentComplete: "% Complete",
    getStartedWithAi:
      "Your brand guidelines are only {percentage}% complete. Use the Build with AI button above to quickly generate your brand identity from a few example captions.",
    getStartedWithAiButton: "Get Started with AI",
    savingChanges: "Saving changes...",
    saveChanges: "Save Changes",
    noBrandSelected: "No brand selected",

    // ColorEditor
    editColor: "Edit Color",
    pickColor: "Pick Color",
    hexValue: "Hex Value",
    colorName: "Color Name",
    contrastPreview: "Contrast Preview",
    whiteText: "White text",
    blackText: "Black text",
    saveColor: "Save Color",

    // TypographyPreview
    headingFont: "Heading Font",
    bodyFont: "Body Font",
    monoFont: "Mono Font",
    headingWeight: "Heading Weight",
    bodyWeight: "Body Weight",
    weight: "Weight:",
    sampleHeading1: "Heading 1 - The quick brown fox",
    sampleHeading2: "Heading 2 - Jumps over the lazy dog",
    sampleHeading3: "Heading 3 - Pack my box with five dozen",
    sampleHeading4: "Heading 4 - Liquor jugs",
    sampleBody:
      "Body text - Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
    sampleSmall:
      "Small text - Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.",
    sampleMono: 'Monospace - const greeting = "Hello, World!";',

    // Typography — Extended Sections
    fontFamilies: "Font Families",
    fontFamiliesDesc: "Primary heading, body, and monospace font families with weights",

    // Type Scale / Hierarchy
    typeScale: "Type Scale",
    typeScaleDesc: "Size, weight, line-height, and letter-spacing for each heading level",
    hierarchyH1: "Heading 1",
    hierarchyH2: "Heading 2",
    hierarchyH3: "Heading 3",
    hierarchyH4: "Heading 4",
    hierarchyBody: "Body",
    hierarchyCaption: "Caption",
    fontSize: "Size",
    fontWeight: "Weight",
    lineHeight: "Line Height",
    letterSpacing: "Letter Spacing",
    fontSizePlaceholder: "e.g. 48px",
    fontWeightPlaceholder: "e.g. 700",
    lineHeightPlaceholder: "e.g. 1.2",
    letterSpacingPlaceholder: "e.g. -0.02em",
    hierarchyPreview: "Preview",
    noHierarchyDefined: "No type scale defined yet. Click Edit to define your type hierarchy.",

    // Typesetting Rules
    typesettingRules: "Typesetting Rules",
    typesettingRulesDesc: "Global spacing, kerning, and line-height defaults",
    paragraphSpacing: "Paragraph Spacing",
    paragraphSpacingPlaceholder: "e.g. 1.5em",
    kerning: "Kerning",
    kerningPlaceholder: "e.g. normal or 0.02em",
    defaultLineHeight: "Default Line Height",
    defaultLineHeightPlaceholder: "e.g. 1.6",
    noTypesettingDefined: "No typesetting rules defined yet.",

    // Font Fallbacks / Web Stack
    fontFallbacks: "Font Fallbacks",
    fontFallbacksDesc: "CSS font-family fallback chains for each font role",
    headingFallbacks: "Heading Fallbacks",
    bodyFallbacks: "Body Fallbacks",
    monoFallbacks: "Mono Fallbacks",
    fallbackPlaceholder: "e.g. Helvetica Neue, Arial, sans-serif",
    cssFontFamily: "CSS font-family",
    noFallbacksDefined: "No font fallbacks defined yet.",
    addFallbackEntry: "Add Fallback",

    // Typography Do's & Don'ts
    typographyDoDont: "Typography Do's & Don'ts",
    typographyDoDontDesc: "Guidelines for proper and improper typography usage",
    typographyDos: "Do's",
    typographyDonts: "Don'ts",
    addTypographyDo: "Add a typography do...",
    addTypographyDont: "Add a typography don't...",
    noTypographyDoDont: "No typography guidelines defined yet.",

    // Font Pairing Preview
    fontPairingPreview: "Font Pairing Preview",
    fontPairingPreviewDesc: "See how your heading, body, and monospace fonts work together in a realistic layout",
    pairingArticleTitle: "The Art of Typography in Brand Design",
    pairingArticleSubtitle: "How font choices shape user perception and brand identity",
    pairingArticleBody1: "Typography is one of the most powerful tools in a designer's arsenal. The right font pairing can communicate authority, warmth, innovation, or tradition without a single word being read consciously.",
    pairingArticleBody2: "When selecting fonts for a brand, consider not just aesthetics but readability across devices, cultural associations, and how the typefaces complement each other at various sizes.",
    pairingArticleCaption: "Figure 1: Font pairing demonstration showing heading and body text interaction",
    pairingCodeSample: "const brand = { fonts: { heading, body, mono } };",

    // LogoSection
    previewBackground: "Preview background:",
    primaryLogo: "Primary Logo",
    iconOnly: "Icon Only",
    wordmark: "Wordmark",
    lightForDarkBg: "Light (for dark bg)",
    darkForLightBg: "Dark (for light bg)",
    noLogoUploaded: "No logo uploaded",
    download: "Download",
    upload: "Upload",
    logoDrafts: "Logo Drafts",
    logoDraftsDesc: "Draft logos from agents or manual uploads",
    logoDraftsEmpty: "No logo drafts yet",
    uploadDraft: "Upload Draft",
    promoteTo: "Promote",
    deleteDraft: "Delete Draft",
    confirmDeleteDraft: "This will permanently delete this logo draft. This action cannot be undone.",
    draftByAgent: "Agent",
    draftByUser: "User",
    generateLogo: "Generate Logo",
    generateLogoDesc: "Use AI to generate a logo based on the brand profile",
    generateLogoTitle: "Generate Logo with AI",
    logoType: "Logo Type",
    logoTypePrimary: "Primary (full logo)",
    logoTypeIcon: "Icon / Symbol only",
    logoTypeWordmark: "Wordmark / Logotype",
    styleDirection: "Style Direction",
    styleDirectionPlaceholder: "e.g. minimalist geometric, playful hand-drawn, bold corporate...",
    additionalNotes: "Additional Notes",
    additionalNotesPlaceholder: "Any specific instructions for the logo...",
    logoTaskCreated: "Logo generation started. The draft will appear here when ready.",
    logoTaskError: "Failed to start logo generation",
    // Quick presets
    quickPresets: "Quick Presets",
    techStartup: "Tech Startup",
    luxury: "Luxury",
    healthcare: "Healthcare",
    creativeAgency: "Creative Agency",
    finance: "Finance",
    organicNatural: "Organic / Natural",
    // Icon styles
    iconStyleLabel: "Icon Style",
    iconAbstract: "Abstract",
    iconGeometric: "Geometric",
    iconLiteral: "Literal",
    iconOrganic: "Organic",
    iconLettermark: "Lettermark",
    // Advanced controls
    advancedControls: "Advanced Controls",
    // Sliders
    complexityLabel: "Complexity",
    minimalist: "Minimalist",
    detailed: "Detailed",
    edgeTreatmentLabel: "Edge Treatment",
    sharp: "Sharp",
    rounded: "Rounded",
    weightLabel: "Weight",
    lightWeight: "Light",
    boldWeight: "Bold",
    // Color approach
    colorApproachLabel: "Color Approach",
    monochrome: "Monochrome",
    limitedColors: "Limited (2-3)",
    fullColor: "Full Color",
    // Typography style
    typographyStyleLabel: "Typography Style",
    sansSerif: "Sans-serif",
    serifStyle: "Serif",
    displayScript: "Display / Script",
    geometricType: "Geometric",
    // Progress steps
    progressCreatingBriefs: "Creating logo briefs...",
    progressDesigning: "AI is designing 3 variations...",
    progressGenerating: "Generating images...",
    progressUploading: "Uploading drafts...",
    progressAlmostDone: "Almost done...",
    progressComplete: "Logo drafts generated!",
    progressTakingLong: "Taking longer than expected. You can close this dialog — drafts will appear when ready.",
    viewDrafts: "View Drafts",
    // Brand context preview
    brandContextPreview: "AI will use your brand profile:",
    noBrandContext: "No brand profile found. AI will generate without brand context.",
    generatingSkeleton: "Generating...",
    enhanceNotes: "Enhance with AI",
    generateNotes: "Generate with AI",
    notesSuggestionPrefix: "AI will create:",
    notesSuggestionWithStyle: "a {logoType} logo in {style} style",
    notesSuggestionNoStyle: "a {logoType} logo",
    notesSuggestionBrandSuffix: "using {brand} brand colors and personality",
    notesSuggestionGenerate: "Click Generate with AI to auto-fill detailed instructions",
    // SVG downloads
    downloadSvg: "Download SVG",
    downloadPng: "Download PNG",
    svgAvailable: "SVG",
    vectorize: "Vectorize",
    vectorizing: "Vectorizing...",

    // LogoSection — New Logo Variants
    secondaryLogo: "Secondary Logo",
    stackedLogo: "Stacked Logo",

    // LogoSection — Collapsible Group Headers
    logoVariantsGroup: "Logo Variants",
    logoVariantsGroupDesc: "All logo versions for different use cases and backgrounds",
    logoGuidelinesGroup: "Logo Guidelines",
    logoGuidelinesGroupDesc: "Usage rules, clear space, minimum sizing, and incorrect uses",
    logoFormatsGroup: "File Formats",
    logoFormatsGroupDesc: "Available file formats per logo variant",

    // LogoSection — Clear Space
    logoClearSpace: "Clear Space",
    logoClearSpaceDesc: "Clear space is the minimum area around the logo that must remain free of other elements to ensure visual impact and legibility.",
    logoClearSpacePlaceholder: "e.g., Maintain a minimum clear space equal to the height of the logo mark on all sides. No text, graphics, or other logos should intrude into this zone...",

    // LogoSection — Minimum Size
    logoMinimumSize: "Minimum Size",
    logoMinimumSizeDesc: "The smallest dimensions at which the logo remains legible and recognizable",
    logoPrintMm: "Print (mm)",
    logoDigitalPx: "Digital (px)",
    logoPrintMmPlaceholder: "e.g., 25",
    logoDigitalPxPlaceholder: "e.g., 80",

    // LogoSection — Incorrect Uses
    logoIncorrectUses: "Incorrect Uses",
    logoIncorrectUsesDesc: "Prohibited modifications and misuses of the logo that must be avoided",
    logoAddIncorrectUse: "Add an incorrect use...",
    logoIncorrectUsePlaceholder: "e.g., Don't stretch or distort the logo proportions",
    logoNoIncorrectUses: "No incorrect uses defined yet.",

    // LogoSection — File Formats
    logoFileFormats: "File Formats",
    logoFileFormatsDesc: "Available file formats per logo variant for download",
    logoFormatAvailable: "Available",
    logoFormatNotAvailable: "Not available",
    logoNoFormats: "No file format information available. Upload or promote logo drafts to populate.",

    // LogoSection — Edit Mode
    logoEditGuidelines: "Edit",
    logoSaveGuidelines: "Save",
    logoCancelEdit: "Cancel",
    logoGuidelinesSaved: "Logo guidelines saved successfully",
    logoGuidelinesFailed: "Failed to save logo guidelines",
    logoNoGuidelinesData: "Not defined yet",

    // Tab labels (shared across editors)
    tabStrategy: "Strategy",
    tabStrategyDesc: "Brand essence, positioning, promise",
    tabMarketResearch: "Market Research",
    tabMarketResearchDesc: "Market intelligence, SWOT, competitive landscape",
    tabPersonality: "Personality",
    tabPersonalityDesc: "Traits, voice, tone",
    tabLogo: "Logo",
    tabLogoDesc: "Logo system and variants",
    tabColors: "Colors",
    tabColorsDesc: "Color palette and usage",
    tabTypography: "Typography",
    tabTypographyDesc: "Fonts and text styles",
    tabAiContext: "AI Context",
    tabAiContextDesc: "Read-only brand summary for AI",
    tabLayout: "Layout",
    tabLayoutDesc: "Spacing and radii",
    tabArtDirection: "Art Direction",
    tabArtDirectionDesc: "Imagery and photography",

    // ColorPalette
    clickToCopy: "Click to copy",
    copied: "Copied!",
    copiedToClipboard: "to clipboard",
    primaryColors: "Primary Colors",
    addPrimaryColor: "Add Primary Color",
    secondaryColors: "Secondary Colors",
    addSecondaryColor: "Add Secondary Color",
    functionalColors: "Functional Colors",
    addSuccess: "Add success",
    addWarning: "Add warning",
    addError: "Add error",
    addInfo: "Add info",

    // Color System — Tints & Shades
    tintsAndShades: "Tints & Shades",
    tintsAndShadesDesc: "Auto-generated lighter and darker variants of your brand colors",
    tint: "Tint",
    shade: "Shade",
    lighterVariants: "Lighter",
    darkerVariants: "Darker",
    baseColor: "Base",

    // Color System — Gradients
    gradients: "Gradients",
    gradientsDesc: "Brand gradients for backgrounds, overlays, and decorative elements",
    addGradient: "Add Gradient",
    editGradient: "Edit Gradient",
    gradientName: "Gradient Name",
    gradientNamePlaceholder: "e.g., Hero Background, CTA Glow",
    gradientType: "Type",
    gradientLinear: "Linear",
    gradientRadial: "Radial",
    gradientAngle: "Angle",
    gradientStops: "Color Stops",
    addStop: "Add Stop",
    gradientUsage: "Usage Context",
    gradientUsagePlaceholder: "e.g., Hero sections, button backgrounds",
    gradientPreview: "Preview",
    noGradients: "No gradients defined yet",
    deleteGradient: "Delete gradient",

    // Color System — Proportions
    colorProportions: "Color Proportions",
    colorProportionsDesc: "Recommended usage percentages for consistent brand application (e.g., 60/30/10 rule)",
    addProportion: "Add Color",
    proportionPercentage: "Usage %",
    proportionUsage: "Used for",
    proportionUsagePlaceholder: "e.g., Backgrounds, large surfaces",
    proportionTotal: "Total",
    noProportions: "No proportions defined yet",

    // Color System — Neutrals
    neutralPalette: "Neutral Palette",
    neutralPaletteDesc: "Grays and neutrals for text, borders, backgrounds, and UI surfaces",
    addNeutral: "Add Neutral",
    noNeutrals: "No neutrals defined yet",

    // Color System — Combinations
    colorCombinations: "Color Combinations",
    colorCombinationsDesc: "Approved color pairings for consistent use across brand materials",
    addCombination: "Add Combination",
    combinationName: "Pairing Name",
    combinationNamePlaceholder: "e.g., Primary CTA, Data Viz Series",
    combinationColors: "Colors",
    combinationContext: "Context",
    combinationContextPlaceholder: "e.g., Marketing materials, UI buttons",
    noCombinations: "No combinations defined yet",
    deleteCombination: "Delete combination",

    // Color System — Accessibility
    colorAccessibility: "Accessibility",
    colorAccessibilityDesc: "WCAG contrast ratios between key color pairs",
    contrastRatio: "Contrast Ratio",
    wcagAA: "AA",
    wcagAAA: "AAA",
    wcagPass: "Pass",
    wcagFail: "Fail",
    normalText: "Normal Text",
    largeText: "Large Text",
    foreground: "Foreground",
    background: "Background",
    contrastOn: "on",

    // ContentListEditor
    noItemsAdded: "No items added yet",

    // ExampleCaptionsEditor
    exampleCaptions: "Example Captions",
    exampleCaptionsDesc:
      "Provide 3-5 example captions that represent your brand's voice. AI will use these as reference for style and tone.",
    example: "Example",
    examplePlaceholder: "Write an example caption that represents your brand's voice...",
    addExampleCaption: "Add Example Caption",
    noExampleCaptions:
      "No example captions added yet. Add 3-5 examples for best AI results.",
    maximum: "Maximum",
    examplesReached: "examples reached",

    // HashtagGroupsEditor
    hashtagGroups: "Hashtag Groups",
    hashtagGroupsDesc: "Organize hashtags into groups: always use, campaign-specific, and blacklisted",
    defaultHashtags: "Default Hashtags",
    defaultHashtagsDesc: "Always include these hashtags in posts",
    campaignHashtags: "Campaign Hashtags",
    campaignHashtagsDesc: "Optional hashtags for special campaigns or events",
    avoidHashtags: "Avoid Hashtags",
    avoidHashtagsDesc: "Never use these hashtags (unless legally required)",
    noHashtagsAdded: "No hashtags added",
    hashtagPlaceholder: "tag1, tag2, tag3...",

    // AIContextViewer
    unableToGenerateContext: "Unable to generate context",
    fields: "fields",
    fillMoreFields: "Fill in more fields for better AI results",
    copyJson: "Copy JSON",
    aiContextSummary: "AI Context Summary",
    aiContextSummaryDesc:
      "This read-only view aggregates your brand data into a structured JSON format for AI agents and MCP tools. Fill in Strategy, Personality, Colors, and Typography tabs to enhance this context.",
    brandContextJson: "brand_context.json",
    contextStrategy: "Strategy",
    contextPersonality: "Personality",
    contextColors: "Colors",
    contextTypography: "Typography",
    brandContextCopied: "Brand context copied to clipboard",
    failedToCopy: "Failed to copy to clipboard",

    // PersonalityEditor
    personalityTraits: "Personality Traits",
    personalityTraitsDesc:
      "Define 3-5 traits that describe how your brand behaves (e.g., precise, innovative, trustworthy)",
    addPersonalityTrait: "Add a personality trait...",
    traits: "traits",
    personalityDos: "Personality Do's",
    personalityDosDesc: "How the personality shows up positively",
    addDo: "Add a do...",
    personalityDonts: "Personality Don'ts",
    personalityDontsDesc: "Behaviors to avoid",
    addDont: "Add a don't...",
    voiceToneRules: "Voice & Tone Rules",
    voiceToneRulesDesc: "Brand voice description, writing style, and formality guidelines",
    personalityBrandVoice: "Brand Voice",
    personalityBrandVoicePlaceholder:
      "Describe your brand's tone, personality, and communication style. e.g., Professional, innovative, and approachable. We speak as industry experts who simplify complexity...",
    writingStyle: "Writing Style",
    writingStylePlaceholder: "e.g., Concise, descriptive...",
    pointOfView: "Point of View",
    pointOfViewPlaceholder: "e.g., First person (we), Second person (you)...",
    formality: "Formality",
    selectFormality: "Select formality",
    formal: "Formal",
    neutral: "Neutral",
    informal: "Informal",
    sampleMessaging: "Sample Messaging",
    sampleMessagingDesc: "Headlines, taglines, and elevator pitch examples",
    headlines: "Headlines",
    addHeadline: "Add a sample headline...",
    taglines: "Taglines",
    addTagline: "Add a sample tagline...",
    elevatorPitch: "Elevator Pitch",
    elevatorPitchPlaceholder: "A 30-second pitch that captures your brand's value proposition...",
    personalityContentDos: "Content Do's",
    personalityContentDosDesc: "Guidelines for AI-generated content",
    personalityContentDosPlaceholder: "e.g., Use action verbs...",
    personalityContentDonts: "Content Don'ts",
    personalityContentDontsDesc: "What to avoid in AI-generated content",
    personalityContentDontsPlaceholder: "e.g., Don't use jargon...",
    personalitySaved: "Personality saved successfully",
    personalityFailed: "Failed to save personality",

    // PersonalityEditor — Section Headers
    sectionPersonalityCore: "Brand Personality",
    sectionPersonalityCoreDesc: "Core personality traits and behavioral guidelines",
    sectionVoiceTone: "Voice & Tone",
    sectionVoiceToneDesc: "How the brand communicates across channels",
    sectionMessaging: "Messaging & Content",
    sectionMessagingDesc: "Sample messaging and content creation guidelines",

    // PersonalityEditor — Target Audience
    personalityTargetAudience: "Target Audience",
    personalityTargetAudienceDesc: "Define who your brand is communicating with",
    personalityTargetAudiencePlaceholder: "Describe your target audience: demographics, psychographics, behaviors, needs, and pain points...",

    // PersonalityEditor — Brand Archetype
    personalityBrandArchetype: "Brand Archetype",
    personalityBrandArchetypeDesc: "Select the Jungian archetype that best represents your brand's character",
    personalityCurrentArchetype: "Current archetype",

    // PersonalityEditor — Tone Dimensions
    personalityToneDimensions: "Tone Dimensions",
    personalityToneDimensionsDesc: "Position your brand on each personality spectrum",
    toneFormal: "Formal",
    toneCasual: "Casual",
    toneSerious: "Serious",
    tonePlayful: "Playful",
    toneAuthoritative: "Authoritative",
    toneApproachable: "Approachable",
    toneReserved: "Reserved",
    toneEnthusiastic: "Enthusiastic",

    // PersonalityEditor — Channel Voice
    personalityChannelVoice: "Channel Voice",
    personalityChannelVoiceDesc: "How your brand voice adapts across different communication channels",
    channelSocialMedia: "Social Media",
    channelSocialMediaPlaceholder: "e.g., Casual and engaging, uses emojis sparingly, conversational...",
    channelEmail: "Email / Newsletter",
    channelEmailPlaceholder: "e.g., Professional but warm, clear CTAs, personalized greetings...",
    channelWebsite: "Website / Blog",
    channelWebsitePlaceholder: "e.g., Authoritative and informative, SEO-conscious, educational...",
    channelFormal: "Formal Documents",
    channelFormalPlaceholder: "e.g., Corporate tone, precise language, third person...",
    channelSupport: "Customer Support",
    channelSupportPlaceholder: "e.g., Empathetic, solution-oriented, patient and clear...",

    // BrandStrategyEditor
    brandEssence: "Brand Essence",
    brandEssenceDesc: "Purpose, mission, vision, and core values that define your brand",
    purpose: "Purpose",
    purposePlaceholder: "Why does the brand exist beyond making money?",
    mission: "Mission",
    missionPlaceholder: "What does the brand do and for whom?",
    vision: "Vision",
    visionPlaceholder: "What future state is the brand working toward?",
    coreValues: "Core Values",
    addCoreValue: "Add a core value...",
    positioningStatement: "Positioning Statement",
    positioningStatementDesc: "How the brand positions itself in the market",
    positioningPlaceholder:
      "For [target audience], [brand] is the [category] that [key benefit] because [reason to believe]...",
    brandPromise: "Brand Promise",
    brandPromiseDesc: "What the brand commits to deliver",
    brandPromisePlaceholder: "The core commitment your brand makes to customers...",
    marketContext: "Market Context",
    marketContextDesc: "Category, competitors, and whitespace analysis",
    category: "Category",
    categoryPlaceholder: "e.g., SaaS, E-commerce, Fintech...",
    competitors: "Competitors",
    addCompetitor: "Add competitor...",
    whitespace: "Whitespace / Opportunity",
    whitespacePlaceholder: "What gap in the market does your brand fill?",
    strategyTargetAudience: "Target Audience",
    strategyTargetAudiencePlaceholder:
      "Who are you speaking to? Describe your ideal customer demographics, behaviors, and needs...",
    brandPillars: "Brand Pillars",
    brandPillarsDesc: "Key themes the brand stands for (3-5 items)",
    addBrandPillar: "Add a brand pillar...",
    pillars: "pillars",
    toneOfLeadership: "Tone of Leadership",
    toneOfLeadershipDesc: "Inspiration, innovation, credibility",
    toneOfLeadershipPlaceholder: "How does your brand lead in its space?",
    strategicNarrative: "Strategic Narrative",
    strategicNarrativeDesc: "The story the brand is built upon",
    strategicNarrativePlaceholder: "The founding story and journey of your brand...",
    strategySaved: "Brand strategy saved successfully",
    strategyFailed: "Failed to save brand strategy",

    // BrandStrategyEditor — Section Group Headers
    sectionBrandFoundation: "Brand Foundation",
    sectionBrandFoundationDesc: "Core identity: essence, promise, and archetype",
    sectionMarketPositioning: "Market Positioning",
    sectionMarketPositioningDesc: "How the brand positions itself and competes in the market",
    sectionBrandPillarsBenefits: "Brand Pillars & Benefits",
    sectionBrandPillarsBenefitsDesc: "Key themes, emotional and functional value propositions",
    sectionBrandVoiceNarrative: "Brand Voice & Narrative",
    sectionBrandVoiceNarrativeDesc: "Leadership tone and brand storytelling",

    // BrandStrategyEditor — Brand Archetype (translated labels)
    brandArchetype: "Brand Archetype",
    brandArchetypeDesc: "The archetype that best represents the brand's personality",
    archetypePlaceholder: "Select an archetype...",
    archetypeInnocent: "The Innocent",
    archetypeSage: "The Sage",
    archetypeExplorer: "The Explorer",
    archetypeRebel: "The Rebel",
    archetypeMagician: "The Magician",
    archetypeHero: "The Hero",
    archetypeLover: "The Lover",
    archetypeJester: "The Jester",
    archetypeEveryman: "The Everyman",
    archetypeCaregiver: "The Caregiver",
    archetypeRuler: "The Ruler",
    archetypeCreator: "The Creator",
    archetypeInnocentDesc: "Optimistic, pure, simple",
    archetypeSageDesc: "Wise, knowledgeable, trusted",
    archetypeExplorerDesc: "Adventurous, independent, pioneering",
    archetypeRebelDesc: "Disruptive, bold, revolutionary",
    archetypeMagicianDesc: "Transformative, visionary, innovative",
    archetypeHeroDesc: "Courageous, determined, inspiring",
    archetypeLoverDesc: "Passionate, intimate, committed",
    archetypeJesterDesc: "Fun, humorous, lighthearted",
    archetypeEverymanDesc: "Relatable, genuine, down-to-earth",
    archetypeCaregiverDesc: "Nurturing, generous, protective",
    archetypeRulerDesc: "Authoritative, commanding, prestigious",
    archetypeCreatorDesc: "Imaginative, expressive, artistic",

    // BrandStrategyEditor — Competitive Advantages (translated)
    competitiveAdvantages: "Competitive Advantages",
    competitiveAdvantagesDesc: "Unique advantages that differentiate the brand",
    addAdvantage: "Add advantage...",

    // BrandStrategyEditor — Emotional & Functional Benefits (translated)
    emotionalBenefits: "Emotional Benefits",
    emotionalBenefitsDesc: "How the customer feels when interacting with the brand",
    addEmotionalBenefit: "Add benefit...",
    functionalBenefits: "Functional Benefits",
    functionalBenefitsDesc: "Tangible, practical benefits the brand delivers",
    addFunctionalBenefit: "Add benefit...",

    // BrandStrategyEditor — Target Segments
    targetSegments: "Target Segments",
    targetSegmentsDesc: "Detailed audience segmentation with demographics and psychographics",
    addSegment: "Add Segment",
    removeSegment: "Remove Segment",
    segmentName: "Segment Name",
    segmentNamePlaceholder: "e.g., Young Professionals, Enterprise Buyers...",
    segmentDescription: "Description",
    segmentDescriptionPlaceholder: "Brief description of this audience segment...",
    segmentDemographics: "Demographics",
    segmentDemographicsPlaceholder: "Age, location, income, education...",
    segmentPsychographics: "Psychographics",
    segmentPsychographicsPlaceholder: "Values, interests, lifestyle, attitudes...",
    segmentPainPoints: "Pain Points",
    addPainPoint: "Add pain point...",
    segmentBehaviors: "Behaviors",
    addBehavior: "Add behavior...",
    noSegments: "No target segments defined yet.",

    // BrandStrategyEditor — Market Sizing
    marketSizing: "Market Sizing",
    marketSizingDesc: "Total addressable, serviceable, and obtainable market estimates",
    tam: "TAM",
    tamFull: "Total Addressable Market",
    tamPlaceholder: "e.g., $50B globally for SaaS in 2026",
    sam: "SAM",
    samFull: "Serviceable Addressable Market",
    samPlaceholder: "e.g., $5B in Latin America enterprise SaaS",
    som: "SOM",
    somFull: "Serviceable Obtainable Market",
    somPlaceholder: "e.g., $50M first-year target in Colombia & Mexico",

    // BrandStrategyEditor — Competitive Gaps & Key Trends
    competitiveGaps: "Competitive Gaps",
    competitiveGapsDesc: "Unmet needs and gaps in competitor offerings",
    addGap: "Add gap...",
    noGaps: "No competitive gaps identified yet.",
    keyTrends: "Key Trends",
    keyTrendsDesc: "Market and industry trends shaping the competitive landscape",
    addTrend: "Add trend...",
    noTrends: "No key trends identified yet.",

    // ArtDirectionEditor
    artDirectionVisualStyleNotes: "Visual Style Notes",
    artDirectionVisualStyleNotesDesc:
      "Guidelines for AI-generated images: describe your visual aesthetic, preferred imagery, and style",
    artDirectionVisualStyleNotesExample:
      "e.g., Clean, modern aesthetic with ample white space. Use warm orange accents sparingly for emphasis. Prefer lifestyle and workspace imagery over abstract graphics. Photography should feel authentic and candid, not overly staged...",
    photographyGuidelines: "Photography Guidelines",
    photographyGuidelinesDesc: "Standards for brand photography including lighting, composition, subjects, and post-processing",
    photographyStandardsPlaceholder: "e.g., Use natural lighting with soft shadows. Avoid harsh flash. Post-processing should maintain true-to-life colors...",
    compositionPlaceholder: "e.g., Rule of thirds, eye-level angles, generous negative space. Hero shots centered, supporting images off-center...",
    subjectMatterPlaceholder: "e.g., Real people in authentic settings. Candid moments preferred over posed shots. Show products in use...",
    lightingPlaceholder: "e.g., Natural, soft, warm",
    tonePlaceholder: "e.g., Warm, inviting, professional",
    saturationPlaceholder: "e.g., Medium-high, vibrant but not oversaturated",
    emotionPlaceholder: "e.g., Confident, approachable, inspiring",
    illustrationStyle: "Illustration Style",
    illustrationStyleDesc: "Guidelines for illustrations including line weights, color usage, and mood",
    illustrationLineWeight: "Line Weight",
    illustrationLineWeightPlaceholder: "e.g., 2px consistent stroke, rounded caps",
    illustrationColorUsage: "Color Usage",
    illustrationColorUsagePlaceholder: "e.g., Use brand primary as dominant fill. Secondary colors for accents. Max 4 colors per illustration...",
    illustrationMood: "Mood",
    illustrationMoodPlaceholder: "e.g., Friendly, modern, slightly playful",
    subjectGuidelines: "Subject Guidelines",
    subjectGuidelinesDesc: "Guidelines for how people, environments, and products should be depicted in brand imagery",
    subjectPeople: "People",
    subjectPeoplePlaceholder: "e.g., Diverse, authentic representation. Show real professionals in their workflow. Age range 25-45...",
    subjectEnvironments: "Environments",
    subjectEnvironmentsPlaceholder: "e.g., Modern workspaces, open plan offices, co-working spaces. Clean, well-lit interiors...",
    subjectProducts: "Products",
    subjectProductsPlaceholder: "e.g., Show products in context of use, not isolated. Lifestyle integration over product-only shots...",
    artDirectionSaved: "Art direction saved successfully",
    artDirectionFailed: "Failed to save art direction",

    // Art Direction — Do's & Don'ts
    artDirectionDoDont: "Art Direction Do's & Don'ts",
    artDirectionDoDontDesc: "Visual guidelines for what to embrace and what to avoid in brand imagery",
    artDirectionDos: "Do's",
    artDirectionDonts: "Don'ts",
    addArtDirectionDo: "Add an art direction do...",
    addArtDirectionDont: "Add an art direction don't...",
    noArtDirectionDoDont: "No art direction guidelines defined yet.",

    // Art Direction — Patterns & Backgrounds
    artDirectionPatterns: "Patterns & Backgrounds",
    artDirectionPatternsDesc: "Approved background styles, textures, and pattern treatments for brand materials",
    artDirectionBackgrounds: "Backgrounds",
    artDirectionTextures: "Textures",
    addBackground: "Add a background style...",
    addTexture: "Add a texture...",
    noPatterns: "No patterns or backgrounds defined yet.",

    // Art Direction — Color Treatment & Filters
    artDirectionColorTreatment: "Color Treatment & Filters",
    artDirectionColorTreatmentDesc: "Image color treatment guidelines including filters, overlays, and duotone effects",
    filterStyle: "Filter Style",
    filterStylePlaceholder: "e.g., warm vintage, cool desaturated, high contrast B&W",
    overlayColor: "Overlay Color",
    overlayColorPlaceholder: "#000000",
    overlayOpacity: "Overlay Opacity",
    overlayOpacityPlaceholder: "e.g., 15",
    duotoneEnabled: "Duotone Effect",
    duotoneColors: "Duotone Colors",
    duotoneColor1Placeholder: "Shadow color",
    duotoneColor2Placeholder: "Highlight color",
    noColorTreatment: "No color treatment defined yet.",

    // Art Direction — Iconography
    artDirectionIconography: "Iconography Guidelines",
    artDirectionIconographyDesc: "Icon system standards including stroke width, corner radius, grid size, and fill style",
    iconStyleField: "Icon Style",
    iconStyleFill: "Fill",
    iconStyleOutline: "Outline",
    iconStyleMixed: "Mixed",
    iconStrokeWidth: "Stroke Width",
    iconStrokeWidthPlaceholder: "e.g., 1.5px, 2px",
    iconCornerRadius: "Corner Radius",
    iconCornerRadiusPlaceholder: "e.g., 2px, 4px",
    iconGridSize: "Grid Size",
    iconGridSizePlaceholder: "e.g., 24x24, 32x32",
    iconNotes: "Notes",
    iconNotesPlaceholder: "Additional iconography guidelines...",
    noIconography: "No iconography guidelines defined yet.",

    // Art Direction — Composition Rules
    artDirectionComposition: "Image Composition Rules",
    artDirectionCompositionDesc: "Approved aspect ratios, cropping guidelines, and safe zone specifications",
    aspectRatios: "Approved Aspect Ratios",
    addAspectRatio: "Add aspect ratio...",
    croppingGuidelines: "Cropping Guidelines",
    croppingGuidelinesPlaceholder: "e.g., Never crop faces above eyebrows. Maintain 10% padding on product shots...",
    safeZones: "Safe Zones / Margins",
    safeZonesPlaceholder: "e.g., Keep logos within inner 80% of frame. 5% text-safe margin on all sides...",
    noCompositionRules: "No composition rules defined yet.",

    // AIBrandBuilder
    aiBrandBuilder: "AI Brand Builder",
    strategyComplete: "Strategy Complete!",
    strategyCompleteDesc:
      "Your brand voice, personality, and content guidelines have been saved.",
    continueToVisualIdentity: "Continue to Visual Identity?",
    continueToVisualIdentityDesc:
      "Design your color palette, typography, and art direction based on the strategy we just created.",
    doneForNow: "Done for now",
    continueToPhase2: "Continue to Phase 2",
    strategySavedToast: "Strategy saved!",
    strategyFailedToast: "Failed to save strategy. Please try again.",
    visualIdentitySaved: "Visual identity saved!",
    visualIdentityFailed: "Failed to save visual identity. Please try again.",

    // PhaseSelector
    phaseSelectorTitle: "AI Brand Builder",
    phaseSelectorDesc:
      "Build your complete brand identity in two phases. Start with strategy, then design your visuals.",
    phase1Title: "Phase 1: Strategy & Core",
    phase1Desc: "Define your brand voice, positioning, and market landscape",
    complete: "Complete",
    recommendedToStart: "Recommended to start",
    phase1LongDesc:
      "Answer questions about your business and provide content examples. AI will generate your brand strategy, market research, personality, and content guidelines.",
    phase1BrandEssence: "Brand Essence",
    phase1Positioning: "Positioning",
    phase1Personality: "Personality",
    phase1VoiceTone: "Voice & Tone",
    phase1ContentRules: "Content Rules",
    phase1MarketResearch: "Market Research",
    textBasedInputs: "Text-based inputs",
    startPhase1: "Start Phase 1",
    regenerate: "Regenerate",
    phase2Title: "Phase 2: Visual Identity",
    phase2Desc: "Design your visual brand system",
    locked: "Locked",
    phase2LongDesc:
      "Select visual preferences and inspirations. AI will generate your color palette, typography, and art direction based on your strategy.",
    phase2Colors: "Colors",
    phase2Typography: "Typography",
    phase2ArtDirection: "Art Direction",
    phase2Photography: "Photography",
    phase2Iconography: "Iconography",
    visualPickers: "Visual pickers",
    startPhase2: "Start Phase 2",
    completePhase1ToUnlock: "Complete Phase 1 to unlock",
    howItWorks: "How it works",
    howItWorksDesc:
      "Phase 1 establishes **who your brand is** through text-based questions. Phase 2 uses that foundation to create **how your brand looks** through visual preferences. Complete both for a comprehensive brand identity.",

    // Phase1
    generatingStrategy: "Generating Your Brand Strategy",
    analyzingCompany: "Analyzing company information...",
    understandingAudience: "Understanding your audience...",
    examiningContent: "Examining content examples...",
    identifyingPersonality: "Identifying brand personality...",
    craftingPositioning: "Crafting positioning statement...",
    buildingContentGuidelines: "Building content guidelines...",
    finalizingStrategy: "Finalizing strategy...",
    failedToGenerateStrategy: "Failed to generate strategy. Please try again.",
    percentCompleteLabel: "% complete",

    // Phase1Input
    phase1InputTitle: "Phase 1: Strategy & Core",
    phase1InputDesc: "Tell us about your business and how you communicate",
    companyName: "Company Name",
    companyNamePlaceholder: "e.g., SOLARIIS",
    industry: "Industry",
    selectIndustry: "Select your industry",
    specifyIndustry: "Specify your industry...",
    whatDoesCompanyDo: "What does your company do?",
    whatDoesCompanyDoPlaceholder:
      "Describe your products/services, what makes you unique, and the value you provide...",
    idealCustomer: "Who is your ideal customer?",
    idealCustomerPlaceholder:
      "Describe your target audience: demographics, needs, pain points, goals...",
    threeWords: "3 words that describe your brand",
    optional: "(optional)",
    threeWordsHelp: "Help guide the AI with personality adjectives",
    threeWordsPlaceholder: "e.g., Bold, Innovative, Trustworthy...",
    keyCompetitors: "Key competitors",
    keyCompetitorsHelp: "Help us understand your competitive landscape",
    keyCompetitorsPlaceholder: "e.g., Competitor A, Competitor B...",
    whatMakesDifferent: "What makes you different?",
    whatMakesDifferentPlaceholder:
      "Your unique value proposition - what sets you apart from competitors...",
    contentExamples: "Content Examples",
    contentExamplesDesc:
      "Share 3-10 pieces of content that represent your brand voice (captions, headlines, copy)",
    enhanceWithAi: "Enhance with AI",
    enhance: "Enhance",
    exampleContent: "Example content",
    addAnotherExample: "Add another example...",
    add: "Add",
    ofMinExamples: "of 3+ examples",
    minimumRequired: "(minimum 3 required)",
    back: "Back",
    generateStrategy: "Generate Strategy",
    generating: "Generating...",
    fillRequiredFields: "Please fill in all required fields",
    enterDescriptionFirst: "Please enter a description first",
    descriptionEnhanced: "Description enhanced!",
    enhancementFailed: "Enhancement failed",
    enterContentFirst: "Please enter content first",
    contentEnhanced: "Content enhanced!",
    failedToEnhanceDescription: "Failed to enhance description",
    failedToEnhanceContent: "Failed to enhance content",

    // Phase1Review
    reviewStrategyCore: "Review Strategy & Core",
    reviewStrategyDesc: "Review each suggestion and accept, edit, or skip to keep existing data.",
    reviewed: "reviewed",
    reviewBrandStrategy: "Brand Strategy",
    reviewBrandEssence: "Brand Essence",
    reviewBrandEssenceDesc: "Purpose, mission, vision, and core values",
    reviewPositioning: "Positioning Statement",
    reviewPositioningDesc: "How your brand differentiates in the market",
    reviewBrandPillars: "Brand Pillars",
    reviewBrandPillarsDesc: "Key themes and focus areas for your brand",
    reviewBrandPromise: "Brand Promise",
    reviewBrandPromiseDesc: "What your brand commits to deliver",
    reviewBrandArchetype: "Brand Archetype",
    reviewBrandArchetypeDesc: "The Jungian archetype that best represents the brand personality",
    reviewMarketContextLabel: "Market Context",
    reviewMarketContextDesc: "Market category, key competitors, and whitespace opportunities",
    reviewCompetitiveAdvantages: "Competitive Advantages",
    reviewCompetitiveAdvantagesDesc: "Key advantages that differentiate the brand from competitors",
    reviewEmotionalBenefits: "Emotional Benefits",
    reviewEmotionalBenefitsDesc: "How the brand makes customers feel",
    reviewFunctionalBenefits: "Functional Benefits",
    reviewFunctionalBenefitsDesc: "Tangible benefits and value the brand delivers",
    reviewToneOfLeadership: "Tone of Leadership",
    reviewToneOfLeadershipDesc: "How the brand leads and communicates authority in its space",
    reviewBrandNarrative: "Brand Narrative",
    reviewBrandNarrativeDesc: "The overarching story that connects the brand to its audience",
    reviewPersonalityVoice: "Personality & Voice",
    reviewBrandVoice: "Brand Voice",
    reviewBrandVoiceDesc: "Overall communication style and tone",
    reviewPersonalityTraits: "Personality Traits",
    reviewPersonalityTraitsDesc: "Adjectives that describe your brand character",
    reviewTargetAudience: "Target Audience",
    reviewTargetAudienceDesc: "Your ideal customer profile",
    reviewVoiceToneRules: "Voice & Tone Rules",
    reviewVoiceToneRulesDesc: "Writing style, point of view, and formality",
    reviewContentGuidelines: "Content Guidelines",
    reviewContentDos: "Content Do's",
    reviewContentDosDesc: "Things to include in your content",
    reviewContentDonts: "Content Don'ts",
    reviewContentDontsDesc: "Things to avoid in your content",
    reviewSuggestedHashtags: "Suggested Hashtags",
    reviewSuggestedHashtagsDesc: "Default hashtags for your content",
    reviewMarketResearch: "Market Research",
    reviewMarketResearchDesc: "Market overview, competitive landscape, and SWOT analysis",
    backToInput: "Back to Input",
    saveContinue: "Save & Continue",

    // Phase2
    designingVisualIdentity: "Designing Your Visual Identity",
    analyzingVisualPreferences: "Analyzing visual preferences...",
    selectingColorPalette: "Selecting color palette...",
    pairingTypography: "Pairing typography...",
    definingArtDirection: "Defining art direction...",
    settingPhotographyGuidelines: "Setting photography guidelines...",
    establishingIconography: "Establishing iconography...",
    finalizingVisualIdentity: "Finalizing visual identity...",
    failedToGenerateVisual: "Failed to generate visual identity. Please try again.",

    // Phase2Input
    phase2InputTitle: "Phase 2: Visual Identity",
    phase2InputDesc: "Select your visual preferences - we'll design your look",
    analyzeUpload: "Analyze Upload",
    analyzeUploadDesc: "Upload a reference file to auto-fill preferences",
    analyzeUploadAccepts: "PDF, PNG, JPG, or SVG (max 5 MB)",
    analyzeUploadAnalyzing: "Extracting visual preferences...",
    analyzeUploadComplete: "{count} fields pre-filled",
    analyzeUploadFailed: "Failed to analyze file. Try a different format or file.",
    analyzeUploadClear: "Clear",
    usingStrategyFrom: "Using strategy from Phase 1:",
    visualSuggestionsAligned: "Visual suggestions will be aligned with your brand personality:",
    colorMood: "Color Mood",
    selectUpTo2: "(select up to 2)",
    colorMoodDesc: "What feeling should your colors evoke?",
    existingBrandColors: "Existing Brand Colors",
    addColorsToKeep: "Add colors you want to keep or build upon",
    hexcodePlaceholder: "#HEXCODE",
    typographyFeel: "Typography Feel",
    selectOne: "(select one)",
    typographyFeelDesc: "What personality should your typography have?",
    photographyStyle: "Photography Style",
    selectUpTo3: "(select up to 3)",
    photographyStyleDesc: "What type of imagery represents your brand?",
    visualInspiration: "Visual Inspiration",
    visualInspirationDesc: "Brands whose visual style you admire",
    whereWillBrandAppear: "Where will your brand appear?",
    selectAllApply: "Select all that apply - helps optimize visual choices",
    visualKeywords: "Visual Keywords",
    visualKeywordsDesc: "Words that describe your desired aesthetic",
    visualKeywordsPlaceholder: "e.g., Minimalist, Luxurious, Organic...",
    describeYourLogo: "Describe Your Logo",
    describeYourLogoPlaceholder:
      "Describe your logo if you have one (style, colors, shape) - helps us suggest complementary visuals...",
    generateVisualIdentity: "Generate Visual Identity",

    // Phase2Review
    reviewVisualIdentity: "Review Visual Identity",
    reviewVisualDesc: "Review each visual suggestion and accept, edit, or skip.",
    colorSystem: "Color System",
    colorPalette: "Color Palette",
    colorPaletteDesc: "Primary and secondary brand colors",
    reviewTypography: "Typography",
    fontSelection: "Font Selection",
    fontSelectionDesc: "Heading, body, and monospace fonts",
    reviewArtDirection: "Art Direction",
    reviewVisualStyleNotes: "Visual Style Notes",
    reviewVisualStyleNotesDesc: "Overall aesthetic direction",
    reviewImageryStyle: "Imagery Style",
    reviewImageryStyleDesc: "Photography and visual standards",
    reviewPhotography: "Photography",
    photoStyle: "Photo Style",
    photoStyleDesc: "Lighting, tone, and mood",
    reviewIconography: "Iconography",
    iconStyle: "Icon Style",
    iconStyleDesc: "Stroke weight and style guidelines",
    saveFinish: "Save & Finish",
    primary: "Primary",
    secondary: "Secondary",
    heading: "Heading",
    body: "Body",
    monospace: "Monospace",
    photographyStandards: "Photography Standards",
    composition: "Composition",
    subjectMatter: "Subject Matter",
    lighting: "Lighting",
    tone: "Tone",
    saturation: "Saturation",
    emotion: "Emotion",
    stroke: "Stroke",
    corners: "Corners",
    style: "Style",
    headingFontLabel: "Heading Font",
    headingWeightLabel: "Heading Weight",
    bodyFontLabel: "Body Font",
    bodyWeightLabel: "Body Weight",
    monospaceFontLabel: "Monospace Font",
    addPrimary: "Add primary",
    addSecondary: "Add secondary",
    colorNameLabel: "Color name",

    // FieldReviewer
    existing: "Existing",
    accepted: "Accepted",
    edited: "Edited",
    skipped: "Skipped",
    skip: "Skip",
    accept: "Accept",
    useNew: "Use New",
    addNewItem: "Add new item...",
    fieldPurpose: "Purpose",
    fieldMission: "Mission",
    fieldVision: "Vision",
    fieldValues: "Values",
    addValue: "Add value...",
    fieldWritingStyle: "Writing Style",
    fieldPointOfView: "Point of View",
    fieldFormality: "Formality",
    fieldFormal: "Formal",
    fieldNeutral: "Neutral",
    fieldInformal: "Informal",

    // MarketResearchView — Section Group Headers
    mrGroupOverview: "Market Overview & Sizing",
    mrGroupOverviewDesc: "Market landscape, size, and growth projections",
    mrGroupCompetitive: "Competitive Landscape",
    mrGroupCompetitiveDesc: "Competitors, market barriers, and positioning",
    mrGroupInternal: "Internal Assessment",
    mrGroupInternalDesc: "SWOT analysis and feasibility evaluation",
    mrGroupRegulatory: "Regulatory & Sources",
    mrGroupRegulatoryDesc: "Digital infrastructure, regulatory notes, sources, and methodology",

    // MarketResearchView — Existing Field Labels
    mrOverview: "Market Overview",
    mrOverviewDesc: "Key trends and market dynamics",
    mrOverviewPlaceholder: "Describe the overall market landscape, key trends, and dynamics...",
    mrSizing: "Market Sizing",
    mrSizingDesc: "Market size and growth potential",
    mrSizingPlaceholder: "Include TAM (Total Addressable Market), SAM (Serviceable Available Market), SOM (Serviceable Obtainable Market), and any relevant revenue estimates...",
    mrCompetitiveLandscape: "Competitive Landscape",
    mrCompetitiveLandscapeDesc: "Key competitors and market positioning",
    mrCompetitiveLandscapePlaceholder: "Describe the competitive landscape, major players, and positioning strategies...",
    mrDigitalInfra: "Digital Infrastructure",
    mrDigitalInfraDesc: "Digital presence and technology channels",
    mrDigitalInfraPlaceholder: "Describe the digital infrastructure, online presence, and technology channels...",
    mrSwotAnalysis: "SWOT Analysis",
    mrSwotAnalysisDesc: "Strengths, weaknesses, opportunities, and threats",
    mrFeasibility: "Feasibility Assessment",
    mrFeasibilityDesc: "Project viability and critical factors",
    mrFeasibilityPlaceholder: "Assess the feasibility: viability, critical factors, resource requirements, risk mitigation...",
    mrRegulatory: "Regulatory Notes",
    mrRegulatoryDesc: "Relevant legal and regulatory considerations",
    mrRegulatoryPlaceholder: "List regulatory requirements, compliance considerations, and legal constraints...",
    mrSources: "Sources",
    mrSourcesDesc: "Research sources and references",

    // MarketResearchView — New Field Labels
    mrKeyCompetitors: "Key Competitors",
    mrKeyCompetitorsDesc: "Detailed competitor profiles with strengths, weaknesses, and market share",
    mrAddCompetitor: "Add Competitor",
    mrRemoveCompetitor: "Remove",
    mrCompetitorName: "Competitor Name",
    mrCompetitorNamePlaceholder: "e.g., Competitor A",
    mrCompetitorStrengths: "Strengths",
    mrCompetitorStrengthsPlaceholder: "Key strengths of this competitor...",
    mrCompetitorWeaknesses: "Weaknesses",
    mrCompetitorWeaknessesPlaceholder: "Key weaknesses or gaps...",
    mrCompetitorMarketShare: "Market Share",
    mrCompetitorMarketSharePlaceholder: "e.g., ~15% of the market",
    mrMarketGrowth: "Market Growth",
    mrMarketGrowthDesc: "CAGR, growth projections, and maturity stage",
    mrMarketGrowthPlaceholder: "Describe market growth rate (CAGR), growth projections, and market maturity stage...",
    mrMarketBarriers: "Market Barriers",
    mrMarketBarriersDesc: "Entry barriers, capital requirements, and switching costs",
    mrMarketBarriersPlaceholder: "Describe barriers to entry, capital requirements, switching costs, and regulatory hurdles...",
    mrResearchMethodology: "Research Methodology",
    mrResearchMethodologyDesc: "Data sources, methodology notes, and confidence levels",
    mrResearchMethodologyPlaceholder: "Describe data sources, research methods, sample sizes, confidence levels, and any limitations...",

    // MarketResearchView — SWOT Labels
    mrStrengths: "Strengths",
    mrWeaknesses: "Weaknesses",
    mrOpportunities: "Opportunities",
    mrThreats: "Threats",

    // MarketResearchView — Edit Mode
    mrEditMode: "Edit",
    mrSaveChanges: "Save",
    mrCancelEdit: "Cancel",
    mrGeneratedAt: "Generated",
    mrNoData: "Not defined yet",
    mrNoPending: "Market research pending - the agent hasn't completed this step yet.",
    mrSaved: "Research saved",
    mrSaveFailed: "Save failed",
    mrAddSource: "Add source...",
    mrAddSwotItem: "Add...",
    mrNoCompetitors: "No competitors profiled yet.",

    // LayoutEditor — Section Group Headers
    layoutSpacingRadii: "Spacing & Radii",
    layoutSpacingRadiiDesc: "Spacing scale tokens and border radius values",
    layoutGridSystem: "Grid System",
    layoutGridSystemDesc: "Column grid specifications for desktop, tablet, and mobile breakpoints",
    layoutMarginsTemplates: "Margins, Padding & Templates",
    layoutMarginsTemplatesDesc: "Page-level margins, padding presets, and layout template references",

    // LayoutEditor — Spacing & Radii Descriptions
    spacingTokensTitle: "Spacing Tokens",
    spacingTokensDesc: "Base spacing scale (xs through 2xl) used for consistent whitespace",
    borderRadiiTitle: "Border Radii",
    borderRadiiDesc: "Corner radius tokens for UI elements (sm, md, lg, full)",

    // LayoutEditor — Grid System
    gridDesktop: "Desktop",
    gridTablet: "Tablet",
    gridMobile: "Mobile",
    gridColumns: "Columns",
    gridGutter: "Gutter",
    gridMargin: "Margin",
    gridColumnsPlaceholder: "e.g. 12",
    gridGutterPlaceholder: "e.g. 24px",
    gridMarginPlaceholder: "e.g. 32px",
    gridEditSpecs: "Edit",
    gridSaveSpecs: "Save",
    gridCancelEdit: "Cancel",
    gridNoSpecs: "No grid specifications defined yet. Click Edit to set up your grid system.",
    gridPreview: "Preview",

    // LayoutEditor — Margins & Padding
    marginsAndPadding: "Margins & Padding",
    marginsAndPaddingDesc: "Common spacing contexts for page-level and component-level spacing",
    marginKey: "Margin",
    paddingKey: "Padding",
    marginValue: "Value",
    paddingValue: "Value",
    addMarginKey: "Add margin key...",
    addPaddingKey: "Add padding key...",
    removeKey: "Remove",
    marginPresets: "Margins",
    paddingPresets: "Padding",
    noMarginsOrPadding: "No margins or padding defined yet.",

    // LayoutEditor — Layout Templates
    layoutTemplates: "Layout Templates",
    layoutTemplatesDesc: "Reusable layout patterns and template references",
    addTemplate: "Add template",
    removeTemplate: "Remove",
    templatePlaceholder: "e.g. Hero + 3-column grid",
    noTemplates: "No layout templates defined yet.",

    // LayoutEditor — Token Labels
    spacingScale: "Spacing Scale",
    radiiScale: "Radii Scale",
    pxEquivalent: "px equivalent",

    // LayoutEditor — Save/status
    layoutSaved: "Layout settings saved",
    layoutSaveFailed: "Failed to save layout settings",

    // AIContextViewer — Enhanced Section Headers
    aiContextOverview: "Context Overview",
    aiContextOverviewDesc: "Completion stats and what AI Context is",
    aiContextStrategyPersonality: "Strategy & Personality Context",
    aiContextStrategyPersonalityDesc: "Compiled strategy and personality data for AI consumption",
    aiContextVisualIdentity: "Visual Identity Context",
    aiContextVisualIdentityDesc: "Colors, typography, and art direction compiled for AI",
    aiContextMarketResearch: "Market & Research Context",
    aiContextMarketResearchDesc: "Market overview, competitive landscape, and key trends",
    aiContextJsonExport: "Full JSON Export",
    aiContextJsonExportDesc: "Raw JSON format for direct use in AI agents and MCP tools",

    // AIContextViewer — Field Labels
    aiNotSet: "Not configured",
    aiSetInTab: "Set in {tab} tab",
    aiFieldPurpose: "Purpose",
    aiFieldMission: "Mission",
    aiFieldVision: "Vision",
    aiFieldValues: "Values",
    aiFieldPositioning: "Positioning",
    aiFieldPromise: "Promise",
    aiFieldArchetype: "Archetype",
    aiFieldPillars: "Pillars",
    aiFieldTargetAudience: "Target Audience",
    aiFieldTargetSegments: "Target Segments",
    aiFieldTraits: "Personality Traits",
    aiFieldVoice: "Voice Summary",
    aiFieldWritingStyle: "Writing Style",
    aiFieldPointOfView: "Point of View",
    aiFieldFormality: "Formality",
    aiFieldToneDimensions: "Tone Dimensions",
    aiFieldChannelVoice: "Channel Voice",
    aiFieldDos: "Do's",
    aiFieldDonts: "Don'ts",
    aiFieldHashtags: "Hashtags",
    aiFieldPrimaryColors: "Primary Colors",
    aiFieldSecondaryColors: "Secondary Colors",
    aiFieldFunctionalColors: "Functional Colors",
    aiFieldHeadingFont: "Heading Font",
    aiFieldBodyFont: "Body Font",
    aiFieldMonoFont: "Mono Font",
    aiFieldWebFallbacks: "Web Fallbacks",
    aiFieldVisualStyle: "Visual Style",
    aiFieldImageryStyle: "Imagery Style",
    aiFieldPhotoStyle: "Photo Style",
    aiFieldIconsStyle: "Icons Style",
    aiFieldMarketOverview: "Market Overview",
    aiFieldCompetitiveLandscape: "Competitive Landscape",
    aiFieldKeyTrends: "Key Trends",

    // AIContextViewer — Section Status Labels (new sections)
    contextArtDirection: "Art Direction",
    contextMarketResearch: "Market Research",

    // AIContextViewer — Completion
    aiContextComplete: "Complete",
    aiContextIncomplete: "Incomplete",
    aiSectionsTracked: "sections tracked",

    // Naming Phase
    namingPhaseTitle: "Brand Naming",
    namingPhaseDesc: "AI-powered name generation with domain checking",
    namingPhaseLongDesc: "Generate creative brand names with phonetic analysis, cultural screening, and real-time domain availability checking.",
    namingBadgeNameIdeas: "Name Ideas",
    namingBadgeDomainCheck: "Domain Check",
    namingBadgeCulturalFit: "Cultural Fit",
    namingBadgeTrademarkScreen: "Trademark Screen",
    namingStartNaming: "Start Naming",
    namingRename: "Rename",
    namingRecommendedForNew: "Recommended for new brands",
    namingCompleteResearchFirst: "Complete Market Research first",
    namingConfigurePrefs: "Configure preferences for AI-powered name generation",
    namingTargetAudience: "Target Audience",
    namingStyles: "Naming Styles",
    namingStylesDesc: "Select the types of names you want generated",
    namingKeywordsInclude: "Keywords to Include",
    namingKeywordsIncludeDesc: "Words or concepts you want reflected in the name",
    namingKeywordsAvoid: "Keywords to Avoid",
    namingLanguageMarkets: "Language Markets",
    namingLanguageMarketsDesc: "Names will be checked for pronunciation and meaning in these languages",
    namingMaxSyllables: "Max Syllables",
    namingDomainTlds: "Domain TLD Preferences",
    namingDomainTldsDesc: "We'll check availability for these domains",
    namingYourIdeas: "Your Name Ideas",
    namingYourIdeasDesc: "We'll evaluate these alongside AI suggestions",
    namingGenerateNames: "Generate Names",
    namingGenerating: "Generating Brand Names...",
    namingAnalyzingMarket: "Analyzing market context...",
    namingGeneratingCandidates: "Generating name candidates...",
    namingExploringStyles: "Exploring naming styles...",
    namingCheckingDomains: "Checking domain availability...",
    namingScoringCandidates: "Scoring candidates...",
    namingRankingResults: "Ranking results...",
    namingNamesGenerated: "Names generated!",
    namingCandidatesTitle: "Name Candidates",
    namingCandidatesDesc: "Ranked by composite score. Click a card to see details, then select your brand name.",
    namingYourIdea: "Your idea",
    namingScore: "Score",
    namingSelect: "Select",
    namingSelected: "Selected",
    namingPhonetic: "Phonetic",
    namingMemorability: "Memorability",
    namingIndustryFit: "Industry Fit",
    namingDifferentiation: "Differentiation",
    namingPronunciation: "Pronunciation",
    namingCulturalNotes: "Cultural Notes",
    namingDomainAvailability: "Domain Availability",
    namingAvailable: "Available",
    namingTaken: "Taken",
    namingTrademarkLow: "Low Risk",
    namingTrademarkMedium: "Medium Risk",
    namingTrademarkHigh: "High Risk",
    namingConfirmName: "Confirm Name",
    namingConfirmQuestion: "Use this as your brand name?",
    namingConfirmDesc: "This will be used for your brand strategy in the next phase.",
    namingRegenerate: "Regenerate",
    namingFromNamingPhase: "From Naming Phase",
    namingBrandNameSelected: "Brand name selected!",
    namingFailedToSave: "Failed to save naming results",

    // Product Pipeline — PRD
    prdTitle: "Product Requirements Document",
    prdGenerateButton: "Generate PRD",
    prdGenerateDescription: "Generate a comprehensive PRD from brand context and client intake responses.",
    prdRegenerate: "Regenerate",
    prdSave: "Save PRD",
    prdSaving: "Saving...",
    prdFeatures: "Features",
    prdProblemStatement: "Problem Statement",
    prdTargetUsers: "Target Users",
    prdInfoArchitecture: "Information Architecture",
    prdTechRequirements: "Technical Requirements",
    prdDesignRequirements: "Design Requirements",
    prdTimeline: "Timeline",
    prdBudget: "Budget Assessment",
    prdSuccessMetrics: "Success Metrics",
    prdRisks: "Risks",
    prdNextSteps: "Next Steps",

    // Product Pipeline — POC
    pocTitle: "Proof of Concept",
    pocDesignBuild: "Design + Build",
    pocDesignBuildDesc: "Generate visual designs first with Stitch, then build the scaffold.",
    pocQuickBuild: "Quick Build",
    pocQuickBuildDesc: "Generate a Next.js code scaffold and deploy directly.",
    pocComingSoon: "Coming Soon",
    pocStartScaffold: "Start Scaffold",
    pocStarting: "Starting...",
    pocGenerating: "Generating scaffold...",
    pocViewPreview: "View Preview",
    pocViewRepo: "View Repository",

    // Unified Image Creator
    imageCreatorTitle: "Image Creator",
    imageCreatorContext: "Context",
    imageCreatorStyle: "Style",
    imageCreatorCustomStyle: "Custom Style",
    imageCreatorCustomStylePlaceholder: "Describe your custom visual style...",
    imageCreatorAspectRatio: "Aspect Ratio",
    imageCreatorPrompt: "Prompt",
    imageCreatorPromptPlaceholder: "Describe the image you want to generate...",
    imageCreatorPromptEnhanced: "Prompt enhanced with AI",
    imageCreatorEnhance: "Enhance Prompt",
    imageCreatorGenerate: "Generate",
    imageCreatorGenerating: "Generating image...",
    imageCreatorSaveToMoodboard: "Save to Moodboard",
    imageCreatorSaved: "Saved to Moodboard",
    imageCreatorUseImage: "Use Image",
    imageCreatorBrandContext: "Brand",
    imageCreatorEmptyPreview: "Your generated image will appear here",
    imageCreatorViewFullSize: "View full size",
    contextMoodboard: "Moodboard",
    contextSocialPost: "Social Post",
    contextBrandTemplate: "Brand Template",
    styleClean: "Clean",
    styleVibrant: "Vibrant",
    styleMinimal: "Minimal",
    styleBold: "Bold",
    styleCustom: "Custom",
  },
  es: {
    // Brand assets page
    loadingBrandAssets: "Cargando activos de marca",
    buildWithAi: "Construir con IA",
    manageBrandGuidelines: "Gestiona la identidad de tu marca en {total} secciones de guías",
    brandGuidelines: "Guías de Marca",
    percentComplete: "% Completo",
    getStartedWithAi:
      "Tus guías de marca están solo al {percentage}% completas. Usa el botón Construir con IA arriba para generar rápidamente tu identidad de marca a partir de algunos subtítulos de ejemplo.",
    getStartedWithAiButton: "Comenzar con IA",
    savingChanges: "Guardando cambios...",
    saveChanges: "Guardar Cambios",
    noBrandSelected: "Sin marca seleccionada",

    // ColorEditor
    editColor: "Editar Color",
    pickColor: "Seleccionar Color",
    hexValue: "Valor Hex",
    colorName: "Nombre del Color",
    contrastPreview: "Vista Previa de Contraste",
    whiteText: "Texto blanco",
    blackText: "Texto negro",
    saveColor: "Guardar Color",

    // TypographyPreview
    headingFont: "Fuente de Título",
    bodyFont: "Fuente de Cuerpo",
    monoFont: "Fuente Monoespaciada",
    headingWeight: "Peso de Título",
    bodyWeight: "Peso de Cuerpo",
    weight: "Peso:",
    sampleHeading1: "Título 1 - El veloz zorro marrón",
    sampleHeading2: "Título 2 - Salta sobre el perro perezoso",
    sampleHeading3: "Título 3 - Empaca mi caja con cinco docenas",
    sampleHeading4: "Título 4 - Jarras de licor",
    sampleBody:
      "Texto de cuerpo - Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
    sampleSmall:
      "Texto pequeño - Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.",
    sampleMono: 'Monoespaciado - const greeting = "Hello, World!";',

    // Typography — Extended Sections
    fontFamilies: "Familias de Fuentes",
    fontFamiliesDesc: "Familias de fuentes primarias para titulos, cuerpo y monoespaciada con pesos",

    // Type Scale / Hierarchy
    typeScale: "Escala Tipografica",
    typeScaleDesc: "Tamano, peso, altura de linea y espaciado de letras para cada nivel de titulo",
    hierarchyH1: "Titulo 1",
    hierarchyH2: "Titulo 2",
    hierarchyH3: "Titulo 3",
    hierarchyH4: "Titulo 4",
    hierarchyBody: "Cuerpo",
    hierarchyCaption: "Leyenda",
    fontSize: "Tamano",
    fontWeight: "Peso",
    lineHeight: "Altura de Linea",
    letterSpacing: "Espaciado de Letras",
    fontSizePlaceholder: "ej. 48px",
    fontWeightPlaceholder: "ej. 700",
    lineHeightPlaceholder: "ej. 1.2",
    letterSpacingPlaceholder: "ej. -0.02em",
    hierarchyPreview: "Vista Previa",
    noHierarchyDefined: "Sin escala tipografica definida. Haz clic en Editar para definir tu jerarquia tipografica.",

    // Typesetting Rules
    typesettingRules: "Reglas de Composicion",
    typesettingRulesDesc: "Espaciado global, kerning y altura de linea predeterminados",
    paragraphSpacing: "Espaciado de Parrafos",
    paragraphSpacingPlaceholder: "ej. 1.5em",
    kerning: "Kerning",
    kerningPlaceholder: "ej. normal o 0.02em",
    defaultLineHeight: "Altura de Linea Predeterminada",
    defaultLineHeightPlaceholder: "ej. 1.6",
    noTypesettingDefined: "Sin reglas de composicion definidas.",

    // Font Fallbacks / Web Stack
    fontFallbacks: "Fuentes de Respaldo",
    fontFallbacksDesc: "Cadenas de respaldo CSS font-family para cada rol de fuente",
    headingFallbacks: "Respaldo de Titulos",
    bodyFallbacks: "Respaldo de Cuerpo",
    monoFallbacks: "Respaldo Mono",
    fallbackPlaceholder: "ej. Helvetica Neue, Arial, sans-serif",
    cssFontFamily: "CSS font-family",
    noFallbacksDefined: "Sin fuentes de respaldo definidas.",
    addFallbackEntry: "Agregar Respaldo",

    // Typography Do's & Don'ts
    typographyDoDont: "Que Hacer y No Hacer en Tipografia",
    typographyDoDontDesc: "Guias para el uso correcto e incorrecto de tipografia",
    typographyDos: "Que Hacer",
    typographyDonts: "Que No Hacer",
    addTypographyDo: "Agregar que hacer...",
    addTypographyDont: "Agregar que no hacer...",
    noTypographyDoDont: "Sin guias tipograficas definidas.",

    // Font Pairing Preview
    fontPairingPreview: "Vista Previa de Combinacion de Fuentes",
    fontPairingPreviewDesc: "Visualiza como funcionan juntas tus fuentes de titulo, cuerpo y monoespaciada en un diseno realista",
    pairingArticleTitle: "El Arte de la Tipografia en el Diseno de Marca",
    pairingArticleSubtitle: "Como las elecciones tipograficas moldean la percepcion del usuario y la identidad de marca",
    pairingArticleBody1: "La tipografia es una de las herramientas mas poderosas en el arsenal de un disenador. La combinacion correcta de fuentes puede comunicar autoridad, calidez, innovacion o tradicion sin que una sola palabra se lea conscientemente.",
    pairingArticleBody2: "Al seleccionar fuentes para una marca, considera no solo la estetica sino la legibilidad en dispositivos, las asociaciones culturales y como los tipos de letra se complementan entre si en varios tamanos.",
    pairingArticleCaption: "Figura 1: Demostracion de combinacion de fuentes mostrando la interaccion entre texto de titulo y cuerpo",
    pairingCodeSample: "const marca = { fuentes: { titulo, cuerpo, mono } };",

    // LogoSection
    previewBackground: "Fondo de vista previa:",
    primaryLogo: "Logo Principal",
    iconOnly: "Solo Icono",
    wordmark: "Marca de Texto",
    lightForDarkBg: "Claro (para fondo oscuro)",
    darkForLightBg: "Oscuro (para fondo claro)",
    noLogoUploaded: "Sin logo cargado",
    download: "Descargar",
    upload: "Cargar",
    logoDrafts: "Borradores de Logo",
    logoDraftsDesc: "Logos borrador de agentes o carga manual",
    logoDraftsEmpty: "Sin borradores de logo aún",
    uploadDraft: "Subir Borrador",
    promoteTo: "Promover",
    deleteDraft: "Eliminar Borrador",
    confirmDeleteDraft: "Esto eliminará permanentemente este borrador de logo. Esta acción no se puede deshacer.",
    draftByAgent: "Agente",
    draftByUser: "Usuario",
    generateLogo: "Generar Logo",
    generateLogoDesc: "Usa IA para generar un logo basado en el perfil de marca",
    generateLogoTitle: "Generar Logo con IA",
    logoType: "Tipo de Logo",
    logoTypePrimary: "Principal (logo completo)",
    logoTypeIcon: "Icono / Solo símbolo",
    logoTypeWordmark: "Logotipo / Solo texto",
    styleDirection: "Dirección de Estilo",
    styleDirectionPlaceholder: "ej. geométrico minimalista, dibujado a mano, corporativo audaz...",
    additionalNotes: "Notas Adicionales",
    additionalNotesPlaceholder: "Instrucciones específicas para el logo...",
    logoTaskCreated: "Generación de logo iniciada. El borrador aparecerá aquí cuando esté listo.",
    logoTaskError: "Error al iniciar la generación de logo",
    // Quick presets
    quickPresets: "Presets Rápidos",
    techStartup: "Tech Startup",
    luxury: "Lujo",
    healthcare: "Salud",
    creativeAgency: "Agencia Creativa",
    finance: "Finanzas",
    organicNatural: "Orgánico / Natural",
    // Icon styles
    iconStyleLabel: "Estilo de Icono",
    iconAbstract: "Abstracto",
    iconGeometric: "Geométrico",
    iconLiteral: "Literal",
    iconOrganic: "Orgánico",
    iconLettermark: "Letramark",
    // Advanced controls
    advancedControls: "Controles Avanzados",
    // Sliders
    complexityLabel: "Complejidad",
    minimalist: "Minimalista",
    detailed: "Detallado",
    edgeTreatmentLabel: "Tratamiento de Bordes",
    sharp: "Afilado",
    rounded: "Redondeado",
    weightLabel: "Peso",
    lightWeight: "Ligero",
    boldWeight: "Grueso",
    // Color approach
    colorApproachLabel: "Enfoque de Color",
    monochrome: "Monocromático",
    limitedColors: "Limitado (2-3)",
    fullColor: "Color Completo",
    // Typography style
    typographyStyleLabel: "Estilo Tipográfico",
    sansSerif: "Sans-serif",
    serifStyle: "Serif",
    displayScript: "Display / Script",
    geometricType: "Geométrico",
    // Progress steps
    progressCreatingBriefs: "Creando briefs de logo...",
    progressDesigning: "La IA está diseñando 3 variaciones...",
    progressGenerating: "Generando imágenes...",
    progressUploading: "Subiendo borradores...",
    progressAlmostDone: "Casi listo...",
    progressComplete: "¡Borradores de logo generados!",
    progressTakingLong: "Está tomando más de lo esperado. Puedes cerrar este diálogo — los borradores aparecerán cuando estén listos.",
    viewDrafts: "Ver Borradores",
    // Brand context preview
    brandContextPreview: "La IA usará tu perfil de marca:",
    noBrandContext: "Sin perfil de marca encontrado. La IA generará sin contexto de marca.",
    generatingSkeleton: "Generando...",
    enhanceNotes: "Mejorar con IA",
    generateNotes: "Generar con IA",
    notesSuggestionPrefix: "La IA creará:",
    notesSuggestionWithStyle: "un logo {logoType} en estilo {style}",
    notesSuggestionNoStyle: "un logo {logoType}",
    notesSuggestionBrandSuffix: "usando los colores y personalidad de {brand}",
    notesSuggestionGenerate: "Haz clic en Generar con IA para auto-completar instrucciones detalladas",
    // SVG downloads
    downloadSvg: "Descargar SVG",
    downloadPng: "Descargar PNG",
    svgAvailable: "SVG",
    vectorize: "Vectorizar",
    vectorizing: "Vectorizando...",

    // LogoSection — New Logo Variants
    secondaryLogo: "Logo Secundario",
    stackedLogo: "Logo Apilado",

    // LogoSection — Collapsible Group Headers
    logoVariantsGroup: "Variantes del Logo",
    logoVariantsGroupDesc: "Todas las versiones del logo para diferentes usos y fondos",
    logoGuidelinesGroup: "Guias del Logo",
    logoGuidelinesGroupDesc: "Reglas de uso, espacio libre, tamano minimo y usos incorrectos",
    logoFormatsGroup: "Formatos de Archivo",
    logoFormatsGroupDesc: "Formatos de archivo disponibles por variante de logo",

    // LogoSection — Clear Space
    logoClearSpace: "Espacio Libre",
    logoClearSpaceDesc: "El espacio libre es el area minima alrededor del logo que debe permanecer libre de otros elementos para asegurar impacto visual y legibilidad.",
    logoClearSpacePlaceholder: "ej., Mantener un espacio libre minimo igual a la altura del icono del logo en todos los lados. Ningun texto, grafico u otro logo debe invadir esta zona...",

    // LogoSection — Minimum Size
    logoMinimumSize: "Tamano Minimo",
    logoMinimumSizeDesc: "Las dimensiones mas pequenas a las que el logo permanece legible y reconocible",
    logoPrintMm: "Impresion (mm)",
    logoDigitalPx: "Digital (px)",
    logoPrintMmPlaceholder: "ej., 25",
    logoDigitalPxPlaceholder: "ej., 80",

    // LogoSection — Incorrect Uses
    logoIncorrectUses: "Usos Incorrectos",
    logoIncorrectUsesDesc: "Modificaciones prohibidas y usos indebidos del logo que deben evitarse",
    logoAddIncorrectUse: "Agregar un uso incorrecto...",
    logoIncorrectUsePlaceholder: "ej., No estirar ni distorsionar las proporciones del logo",
    logoNoIncorrectUses: "Sin usos incorrectos definidos.",

    // LogoSection — File Formats
    logoFileFormats: "Formatos de Archivo",
    logoFileFormatsDesc: "Formatos de archivo disponibles por variante de logo para descarga",
    logoFormatAvailable: "Disponible",
    logoFormatNotAvailable: "No disponible",
    logoNoFormats: "Sin informacion de formatos de archivo. Sube o promueve borradores de logo para llenar.",

    // LogoSection — Edit Mode
    logoEditGuidelines: "Editar",
    logoSaveGuidelines: "Guardar",
    logoCancelEdit: "Cancelar",
    logoGuidelinesSaved: "Guias del logo guardadas exitosamente",
    logoGuidelinesFailed: "Error al guardar las guias del logo",
    logoNoGuidelinesData: "Sin definir",

    // Tab labels (shared across editors)
    tabStrategy: "Estrategia",
    tabStrategyDesc: "Esencia, posicionamiento, promesa de marca",
    tabMarketResearch: "Investigación de Mercado",
    tabMarketResearchDesc: "Inteligencia de mercado, FODA, panorama competitivo",
    tabPersonality: "Personalidad",
    tabPersonalityDesc: "Rasgos, voz, tono",
    tabLogo: "Logo",
    tabLogoDesc: "Sistema de logo y variantes",
    tabColors: "Colores",
    tabColorsDesc: "Paleta de colores y uso",
    tabTypography: "Tipografía",
    tabTypographyDesc: "Fuentes y estilos de texto",
    tabAiContext: "Contexto IA",
    tabAiContextDesc: "Resumen de marca para IA (solo lectura)",
    tabLayout: "Diseño",
    tabLayoutDesc: "Espaciado y radios",
    tabArtDirection: "Dirección de Arte",
    tabArtDirectionDesc: "Imágenes y fotografía",

    // ColorPalette
    clickToCopy: "Clic para copiar",
    copied: "¡Copiado!",
    copiedToClipboard: "al portapapeles",
    primaryColors: "Colores Primarios",
    addPrimaryColor: "Agregar Color Primario",
    secondaryColors: "Colores Secundarios",
    addSecondaryColor: "Agregar Color Secundario",
    functionalColors: "Colores Funcionales",
    addSuccess: "Agregar éxito",
    addWarning: "Agregar advertencia",
    addError: "Agregar error",
    addInfo: "Agregar info",

    // Color System — Tints & Shades
    tintsAndShades: "Tintes y Sombras",
    tintsAndShadesDesc: "Variantes claras y oscuras generadas automáticamente de tus colores de marca",
    tint: "Tinte",
    shade: "Sombra",
    lighterVariants: "Más claro",
    darkerVariants: "Más oscuro",
    baseColor: "Base",

    // Color System — Gradients
    gradients: "Degradados",
    gradientsDesc: "Degradados de marca para fondos, superposiciones y elementos decorativos",
    addGradient: "Agregar Degradado",
    editGradient: "Editar Degradado",
    gradientName: "Nombre del Degradado",
    gradientNamePlaceholder: "ej., Fondo Hero, Brillo CTA",
    gradientType: "Tipo",
    gradientLinear: "Lineal",
    gradientRadial: "Radial",
    gradientAngle: "Ángulo",
    gradientStops: "Paradas de Color",
    addStop: "Agregar Parada",
    gradientUsage: "Contexto de Uso",
    gradientUsagePlaceholder: "ej., Secciones hero, fondos de botones",
    gradientPreview: "Vista previa",
    noGradients: "Sin degradados definidos aún",
    deleteGradient: "Eliminar degradado",

    // Color System — Proportions
    colorProportions: "Proporciones de Color",
    colorProportionsDesc: "Porcentajes de uso recomendados para aplicación consistente de marca (ej., regla 60/30/10)",
    addProportion: "Agregar Color",
    proportionPercentage: "Uso %",
    proportionUsage: "Usado para",
    proportionUsagePlaceholder: "ej., Fondos, superficies grandes",
    proportionTotal: "Total",
    noProportions: "Sin proporciones definidas aún",

    // Color System — Neutrals
    neutralPalette: "Paleta Neutral",
    neutralPaletteDesc: "Grises y neutrales para texto, bordes, fondos y superficies de UI",
    addNeutral: "Agregar Neutral",
    noNeutrals: "Sin neutrales definidos aún",

    // Color System — Combinations
    colorCombinations: "Combinaciones de Color",
    colorCombinationsDesc: "Pares de colores aprobados para uso consistente en materiales de marca",
    addCombination: "Agregar Combinación",
    combinationName: "Nombre del Par",
    combinationNamePlaceholder: "ej., CTA Primario, Serie Data Viz",
    combinationColors: "Colores",
    combinationContext: "Contexto",
    combinationContextPlaceholder: "ej., Materiales de marketing, botones de UI",
    noCombinations: "Sin combinaciones definidas aún",
    deleteCombination: "Eliminar combinación",

    // Color System — Accessibility
    colorAccessibility: "Accesibilidad",
    colorAccessibilityDesc: "Ratios de contraste WCAG entre pares de colores clave",
    contrastRatio: "Ratio de Contraste",
    wcagAA: "AA",
    wcagAAA: "AAA",
    wcagPass: "Pasa",
    wcagFail: "Falla",
    normalText: "Texto Normal",
    largeText: "Texto Grande",
    foreground: "Primer plano",
    background: "Fondo",
    contrastOn: "sobre",

    // ContentListEditor
    noItemsAdded: "Sin elementos agregados aún",

    // ExampleCaptionsEditor
    exampleCaptions: "Subtítulos de Ejemplo",
    exampleCaptionsDesc:
      "Proporciona 3-5 subtítulos de ejemplo que representen la voz de tu marca. La IA los usará como referencia de estilo y tono.",
    example: "Ejemplo",
    examplePlaceholder: "Escribe un subtítulo de ejemplo que represente la voz de tu marca...",
    addExampleCaption: "Agregar Subtítulo de Ejemplo",
    noExampleCaptions:
      "Sin subtítulos de ejemplo aún. Agrega 3-5 ejemplos para mejores resultados de IA.",
    maximum: "Máximo",
    examplesReached: "ejemplos alcanzados",

    // HashtagGroupsEditor
    hashtagGroups: "Grupos de Hashtags",
    hashtagGroupsDesc: "Organiza hashtags en grupos: uso permanente, campañas y lista negra",
    defaultHashtags: "Hashtags Predeterminados",
    defaultHashtagsDesc: "Siempre incluir estos hashtags en publicaciones",
    campaignHashtags: "Hashtags de Campaña",
    campaignHashtagsDesc: "Hashtags opcionales para campañas o eventos especiales",
    avoidHashtags: "Hashtags a Evitar",
    avoidHashtagsDesc: "Nunca usar estos hashtags (salvo requisito legal)",
    noHashtagsAdded: "Sin hashtags agregados",
    hashtagPlaceholder: "tag1, tag2, tag3...",

    // AIContextViewer
    unableToGenerateContext: "No se puede generar el contexto",
    fields: "campos",
    fillMoreFields: "Completa más campos para mejores resultados de IA",
    copyJson: "Copiar JSON",
    aiContextSummary: "Resumen de Contexto IA",
    aiContextSummaryDesc:
      "Esta vista de solo lectura agrega los datos de tu marca en formato JSON estructurado para agentes IA y herramientas MCP. Completa las pestañas de Estrategia, Personalidad, Colores y Tipografía para mejorar este contexto.",
    brandContextJson: "brand_context.json",
    contextStrategy: "Estrategia",
    contextPersonality: "Personalidad",
    contextColors: "Colores",
    contextTypography: "Tipografía",
    brandContextCopied: "Contexto de marca copiado al portapapeles",
    failedToCopy: "Error al copiar al portapapeles",

    // PersonalityEditor
    personalityTraits: "Rasgos de Personalidad",
    personalityTraitsDesc:
      "Define 3-5 rasgos que describan cómo se comporta tu marca (ej., precisa, innovadora, confiable)",
    addPersonalityTrait: "Agregar un rasgo de personalidad...",
    traits: "rasgos",
    personalityDos: "Qué Hacer",
    personalityDosDesc: "Cómo se manifiesta la personalidad positivamente",
    addDo: "Agregar un qué hacer...",
    personalityDonts: "Qué No Hacer",
    personalityDontsDesc: "Comportamientos a evitar",
    addDont: "Agregar un qué no hacer...",
    voiceToneRules: "Reglas de Voz y Tono",
    voiceToneRulesDesc: "Descripción de la voz de marca, estilo de escritura y guías de formalidad",
    personalityBrandVoice: "Voz de Marca",
    personalityBrandVoicePlaceholder:
      "Describe el tono, personalidad y estilo de comunicación de tu marca. ej., Profesional, innovador y accesible. Hablamos como expertos de la industria que simplifican la complejidad...",
    writingStyle: "Estilo de Escritura",
    writingStylePlaceholder: "ej., Conciso, descriptivo...",
    pointOfView: "Punto de Vista",
    pointOfViewPlaceholder: "ej., Primera persona (nosotros), Segunda persona (tú)...",
    formality: "Formalidad",
    selectFormality: "Seleccionar formalidad",
    formal: "Formal",
    neutral: "Neutral",
    informal: "Informal",
    sampleMessaging: "Mensajes de Ejemplo",
    sampleMessagingDesc: "Titulares, eslóganes y ejemplos de pitch",
    headlines: "Titulares",
    addHeadline: "Agregar un titular de ejemplo...",
    taglines: "Eslóganes",
    addTagline: "Agregar un eslogan de ejemplo...",
    elevatorPitch: "Elevator Pitch",
    elevatorPitchPlaceholder: "Un pitch de 30 segundos que capture la propuesta de valor de tu marca...",
    personalityContentDos: "Qué Hacer en Contenido",
    personalityContentDosDesc: "Guías para contenido generado por IA",
    personalityContentDosPlaceholder: "ej., Usar verbos de acción...",
    personalityContentDonts: "Qué No Hacer en Contenido",
    personalityContentDontsDesc: "Qué evitar en contenido generado por IA",
    personalityContentDontsPlaceholder: "ej., No usar jerga...",
    personalitySaved: "Personalidad guardada exitosamente",
    personalityFailed: "Error al guardar la personalidad",

    // PersonalityEditor — Section Headers
    sectionPersonalityCore: "Personalidad de Marca",
    sectionPersonalityCoreDesc: "Rasgos de personalidad y guias de comportamiento",
    sectionVoiceTone: "Voz y Tono",
    sectionVoiceToneDesc: "Como se comunica la marca en diferentes canales",
    sectionMessaging: "Mensajeria y Contenido",
    sectionMessagingDesc: "Mensajes de ejemplo y guias de creacion de contenido",

    // PersonalityEditor — Target Audience
    personalityTargetAudience: "Audiencia Objetivo",
    personalityTargetAudienceDesc: "Define con quien se comunica tu marca",
    personalityTargetAudiencePlaceholder: "Describe tu audiencia objetivo: demografia, psicografia, comportamientos, necesidades y puntos de dolor...",

    // PersonalityEditor — Brand Archetype
    personalityBrandArchetype: "Arquetipo de Marca",
    personalityBrandArchetypeDesc: "Selecciona el arquetipo junguiano que mejor representa el caracter de tu marca",
    personalityCurrentArchetype: "Arquetipo actual",

    // PersonalityEditor — Tone Dimensions
    personalityToneDimensions: "Dimensiones del Tono",
    personalityToneDimensionsDesc: "Posiciona tu marca en cada espectro de personalidad",
    toneFormal: "Formal",
    toneCasual: "Casual",
    toneSerious: "Serio",
    tonePlayful: "Jugueton",
    toneAuthoritative: "Autoritativo",
    toneApproachable: "Accesible",
    toneReserved: "Reservado",
    toneEnthusiastic: "Entusiasta",

    // PersonalityEditor — Channel Voice
    personalityChannelVoice: "Voz por Canal",
    personalityChannelVoiceDesc: "Como se adapta la voz de tu marca en diferentes canales de comunicacion",
    channelSocialMedia: "Redes Sociales",
    channelSocialMediaPlaceholder: "ej., Casual y atractivo, usa emojis con moderacion, conversacional...",
    channelEmail: "Email / Newsletter",
    channelEmailPlaceholder: "ej., Profesional pero calido, CTAs claros, saludos personalizados...",
    channelWebsite: "Sitio Web / Blog",
    channelWebsitePlaceholder: "ej., Autoritativo e informativo, consciente del SEO, educativo...",
    channelFormal: "Documentos Formales",
    channelFormalPlaceholder: "ej., Tono corporativo, lenguaje preciso, tercera persona...",
    channelSupport: "Soporte al Cliente",
    channelSupportPlaceholder: "ej., Empatico, orientado a soluciones, paciente y claro...",

    // BrandStrategyEditor
    brandEssence: "Esencia de Marca",
    brandEssenceDesc: "Propósito, misión, visión y valores fundamentales que definen tu marca",
    purpose: "Propósito",
    purposePlaceholder: "¿Por qué existe la marca más allá de ganar dinero?",
    mission: "Misión",
    missionPlaceholder: "¿Qué hace la marca y para quién?",
    vision: "Visión",
    visionPlaceholder: "¿Hacia qué estado futuro trabaja la marca?",
    coreValues: "Valores Fundamentales",
    addCoreValue: "Agregar un valor fundamental...",
    positioningStatement: "Declaración de Posicionamiento",
    positioningStatementDesc: "Cómo se posiciona la marca en el mercado",
    positioningPlaceholder:
      "Para [audiencia objetivo], [marca] es la [categoría] que [beneficio clave] porque [razón para creer]...",
    brandPromise: "Promesa de Marca",
    brandPromiseDesc: "Lo que la marca se compromete a entregar",
    brandPromisePlaceholder: "El compromiso central que tu marca hace con los clientes...",
    marketContext: "Contexto de Mercado",
    marketContextDesc: "Categoría, competidores y análisis de oportunidades",
    category: "Categoría",
    categoryPlaceholder: "ej., SaaS, E-commerce, Fintech...",
    competitors: "Competidores",
    addCompetitor: "Agregar competidor...",
    whitespace: "Espacio / Oportunidad",
    whitespacePlaceholder: "¿Qué brecha en el mercado llena tu marca?",
    strategyTargetAudience: "Audiencia Objetivo",
    strategyTargetAudiencePlaceholder:
      "¿A quién le hablas? Describe la demografía, comportamientos y necesidades de tu cliente ideal...",
    brandPillars: "Pilares de Marca",
    brandPillarsDesc: "Temas clave que la marca representa (3-5 elementos)",
    addBrandPillar: "Agregar un pilar de marca...",
    pillars: "pilares",
    toneOfLeadership: "Tono de Liderazgo",
    toneOfLeadershipDesc: "Inspiración, innovación, credibilidad",
    toneOfLeadershipPlaceholder: "¿Cómo lidera tu marca en su espacio?",
    strategicNarrative: "Narrativa Estratégica",
    strategicNarrativeDesc: "La historia sobre la que se construye la marca",
    strategicNarrativePlaceholder: "La historia fundacional y el viaje de tu marca...",
    strategySaved: "Estrategia de marca guardada exitosamente",
    strategyFailed: "Error al guardar la estrategia de marca",

    // BrandStrategyEditor — Section Group Headers
    sectionBrandFoundation: "Fundamentos de Marca",
    sectionBrandFoundationDesc: "Identidad central: esencia, promesa y arquetipo",
    sectionMarketPositioning: "Posicionamiento de Mercado",
    sectionMarketPositioningDesc: "Como se posiciona y compite la marca en el mercado",
    sectionBrandPillarsBenefits: "Pilares y Beneficios de Marca",
    sectionBrandPillarsBenefitsDesc: "Temas clave, propuestas de valor emocional y funcional",
    sectionBrandVoiceNarrative: "Voz y Narrativa de Marca",
    sectionBrandVoiceNarrativeDesc: "Tono de liderazgo y narrativa de marca",

    // BrandStrategyEditor — Brand Archetype (translated labels)
    brandArchetype: "Arquetipo de Marca",
    brandArchetypeDesc: "El arquetipo que mejor representa la personalidad de la marca",
    archetypePlaceholder: "Seleccionar un arquetipo...",
    archetypeInnocent: "El Inocente",
    archetypeSage: "El Sabio",
    archetypeExplorer: "El Explorador",
    archetypeRebel: "El Rebelde",
    archetypeMagician: "El Mago",
    archetypeHero: "El Heroe",
    archetypeLover: "El Amante",
    archetypeJester: "El Bufon",
    archetypeEveryman: "El Ciudadano",
    archetypeCaregiver: "El Cuidador",
    archetypeRuler: "El Gobernante",
    archetypeCreator: "El Creador",
    archetypeInnocentDesc: "Optimista, puro, simple",
    archetypeSageDesc: "Sabio, conocedor, confiable",
    archetypeExplorerDesc: "Aventurero, independiente, pionero",
    archetypeRebelDesc: "Disruptivo, audaz, revolucionario",
    archetypeMagicianDesc: "Transformador, visionario, innovador",
    archetypeHeroDesc: "Valiente, determinado, inspirador",
    archetypeLoverDesc: "Apasionado, intimo, comprometido",
    archetypeJesterDesc: "Divertido, humoristico, desenfadado",
    archetypeEverymanDesc: "Cercano, genuino, con los pies en la tierra",
    archetypeCaregiverDesc: "Protector, generoso, compasivo",
    archetypeRulerDesc: "Autoritario, influyente, prestigioso",
    archetypeCreatorDesc: "Imaginativo, expresivo, artistico",

    // BrandStrategyEditor — Competitive Advantages (translated)
    competitiveAdvantages: "Ventajas Competitivas",
    competitiveAdvantagesDesc: "Ventajas unicas que diferencian la marca",
    addAdvantage: "Agregar ventaja...",

    // BrandStrategyEditor — Emotional & Functional Benefits (translated)
    emotionalBenefits: "Beneficios Emocionales",
    emotionalBenefitsDesc: "Como se siente el cliente al interactuar con la marca",
    addEmotionalBenefit: "Agregar beneficio...",
    functionalBenefits: "Beneficios Funcionales",
    functionalBenefitsDesc: "Beneficios tangibles y practicos que ofrece la marca",
    addFunctionalBenefit: "Agregar beneficio...",

    // BrandStrategyEditor — Target Segments
    targetSegments: "Segmentos Objetivo",
    targetSegmentsDesc: "Segmentacion detallada de audiencia con datos demograficos y psicograficos",
    addSegment: "Agregar Segmento",
    removeSegment: "Eliminar Segmento",
    segmentName: "Nombre del Segmento",
    segmentNamePlaceholder: "ej., Jovenes Profesionales, Compradores Corporativos...",
    segmentDescription: "Descripcion",
    segmentDescriptionPlaceholder: "Breve descripcion de este segmento de audiencia...",
    segmentDemographics: "Demografia",
    segmentDemographicsPlaceholder: "Edad, ubicacion, ingresos, educacion...",
    segmentPsychographics: "Psicografia",
    segmentPsychographicsPlaceholder: "Valores, intereses, estilo de vida, actitudes...",
    segmentPainPoints: "Puntos de Dolor",
    addPainPoint: "Agregar punto de dolor...",
    segmentBehaviors: "Comportamientos",
    addBehavior: "Agregar comportamiento...",
    noSegments: "Sin segmentos objetivo definidos.",

    // BrandStrategyEditor — Market Sizing
    marketSizing: "Dimensionamiento de Mercado",
    marketSizingDesc: "Estimaciones de mercado total direccionable, servible y obtenible",
    tam: "TAM",
    tamFull: "Mercado Total Direccionable",
    tamPlaceholder: "ej., $50B globalmente para SaaS en 2026",
    sam: "SAM",
    samFull: "Mercado Direccionable Servible",
    samPlaceholder: "ej., $5B en SaaS empresarial en Latinoamerica",
    som: "SOM",
    somFull: "Mercado Obtenible Servible",
    somPlaceholder: "ej., $50M objetivo primer ano en Colombia y Mexico",

    // BrandStrategyEditor — Competitive Gaps & Key Trends
    competitiveGaps: "Brechas Competitivas",
    competitiveGapsDesc: "Necesidades insatisfechas y brechas en ofertas de competidores",
    addGap: "Agregar brecha...",
    noGaps: "Sin brechas competitivas identificadas.",
    keyTrends: "Tendencias Clave",
    keyTrendsDesc: "Tendencias de mercado e industria que moldean el panorama competitivo",
    addTrend: "Agregar tendencia...",
    noTrends: "Sin tendencias clave identificadas.",

    // ArtDirectionEditor
    artDirectionVisualStyleNotes: "Notas de Estilo Visual",
    artDirectionVisualStyleNotesDesc:
      "Guías para imágenes generadas por IA: describe tu estética visual, imágenes preferidas y estilo",
    artDirectionVisualStyleNotesExample:
      "ej., Estética limpia y moderna con amplio espacio en blanco. Usa acentos naranja cálidos con moderación. Prefiere imágenes de estilo de vida sobre gráficos abstractos. La fotografía debe sentirse auténtica y espontánea, no demasiado montada...",
    photographyGuidelines: "Guías de Fotografía",
    photographyGuidelinesDesc: "Estándares de fotografía de marca incluyendo iluminación, composición, sujetos y post-procesamiento",
    photographyStandardsPlaceholder: "ej., Usar iluminación natural con sombras suaves. Evitar flash directo. El post-procesamiento debe mantener colores fieles a la realidad...",
    compositionPlaceholder: "ej., Regla de tercios, ángulos a nivel de ojos, espacio negativo generoso. Tomas principales centradas, imágenes de apoyo descentradas...",
    subjectMatterPlaceholder: "ej., Personas reales en entornos auténticos. Momentos espontáneos sobre poses. Mostrar productos en uso...",
    lightingPlaceholder: "ej., Natural, suave, cálida",
    tonePlaceholder: "ej., Cálido, acogedor, profesional",
    saturationPlaceholder: "ej., Media-alta, vibrante pero no sobresaturada",
    emotionPlaceholder: "ej., Confiada, accesible, inspiradora",
    illustrationStyle: "Estilo de Ilustración",
    illustrationStyleDesc: "Guías de ilustración incluyendo pesos de línea, uso de color y estado de ánimo",
    illustrationLineWeight: "Peso de Línea",
    illustrationLineWeightPlaceholder: "ej., Trazo consistente de 2px, terminaciones redondeadas",
    illustrationColorUsage: "Uso del Color",
    illustrationColorUsagePlaceholder: "ej., Usar color primario de marca como relleno dominante. Colores secundarios para acentos. Máximo 4 colores por ilustración...",
    illustrationMood: "Estado de Ánimo",
    illustrationMoodPlaceholder: "ej., Amigable, moderno, ligeramente lúdico",
    subjectGuidelines: "Guías de Sujeto",
    subjectGuidelinesDesc: "Guías de cómo representar personas, entornos y productos en la imagen de marca",
    subjectPeople: "Personas",
    subjectPeoplePlaceholder: "ej., Representación diversa y auténtica. Mostrar profesionales reales en su flujo de trabajo. Rango de edad 25-45...",
    subjectEnvironments: "Entornos",
    subjectEnvironmentsPlaceholder: "ej., Espacios de trabajo modernos, oficinas abiertas, co-working. Interiores limpios y bien iluminados...",
    subjectProducts: "Productos",
    subjectProductsPlaceholder: "ej., Mostrar productos en contexto de uso, no aislados. Integración con estilo de vida sobre tomas solo de producto...",
    artDirectionSaved: "Direccion de arte guardada exitosamente",
    artDirectionFailed: "Error al guardar la direccion de arte",

    // Art Direction — Do's & Don'ts
    artDirectionDoDont: "Que Hacer y No Hacer en Direccion de Arte",
    artDirectionDoDontDesc: "Guias visuales sobre que adoptar y que evitar en la imagen de marca",
    artDirectionDos: "Que Hacer",
    artDirectionDonts: "Que No Hacer",
    addArtDirectionDo: "Agregar que hacer...",
    addArtDirectionDont: "Agregar que no hacer...",
    noArtDirectionDoDont: "Sin guias de direccion de arte definidas.",

    // Art Direction — Patterns & Backgrounds
    artDirectionPatterns: "Patrones y Fondos",
    artDirectionPatternsDesc: "Estilos de fondo, texturas y tratamientos de patrones aprobados para materiales de marca",
    artDirectionBackgrounds: "Fondos",
    artDirectionTextures: "Texturas",
    addBackground: "Agregar un estilo de fondo...",
    addTexture: "Agregar una textura...",
    noPatterns: "Sin patrones o fondos definidos.",

    // Art Direction — Color Treatment & Filters
    artDirectionColorTreatment: "Tratamiento de Color y Filtros",
    artDirectionColorTreatmentDesc: "Guias de tratamiento de color en imagenes incluyendo filtros, superposiciones y efectos duotono",
    filterStyle: "Estilo de Filtro",
    filterStylePlaceholder: "ej., vintage calido, desaturado frio, alto contraste B&N",
    overlayColor: "Color de Superposicion",
    overlayColorPlaceholder: "#000000",
    overlayOpacity: "Opacidad de Superposicion",
    overlayOpacityPlaceholder: "ej., 15",
    duotoneEnabled: "Efecto Duotono",
    duotoneColors: "Colores Duotono",
    duotoneColor1Placeholder: "Color de sombra",
    duotoneColor2Placeholder: "Color de luz",
    noColorTreatment: "Sin tratamiento de color definido.",

    // Art Direction — Iconography
    artDirectionIconography: "Guias de Iconografia",
    artDirectionIconographyDesc: "Estandares del sistema de iconos incluyendo ancho de trazo, radio de esquina, tamano de cuadricula y estilo de relleno",
    iconStyleField: "Estilo de Icono",
    iconStyleFill: "Relleno",
    iconStyleOutline: "Contorno",
    iconStyleMixed: "Mixto",
    iconStrokeWidth: "Ancho de Trazo",
    iconStrokeWidthPlaceholder: "ej., 1.5px, 2px",
    iconCornerRadius: "Radio de Esquina",
    iconCornerRadiusPlaceholder: "ej., 2px, 4px",
    iconGridSize: "Tamano de Cuadricula",
    iconGridSizePlaceholder: "ej., 24x24, 32x32",
    iconNotes: "Notas",
    iconNotesPlaceholder: "Guias adicionales de iconografia...",
    noIconography: "Sin guias de iconografia definidas.",

    // Art Direction — Composition Rules
    artDirectionComposition: "Reglas de Composicion de Imagen",
    artDirectionCompositionDesc: "Relaciones de aspecto aprobadas, guias de recorte y especificaciones de zonas seguras",
    aspectRatios: "Relaciones de Aspecto Aprobadas",
    addAspectRatio: "Agregar relacion de aspecto...",
    croppingGuidelines: "Guias de Recorte",
    croppingGuidelinesPlaceholder: "ej., Nunca recortar rostros por encima de las cejas. Mantener 10% de relleno en tomas de producto...",
    safeZones: "Zonas Seguras / Margenes",
    safeZonesPlaceholder: "ej., Mantener logos dentro del 80% interior del cuadro. 5% de margen seguro para texto en todos los lados...",
    noCompositionRules: "Sin reglas de composicion definidas.",

    // AIBrandBuilder
    aiBrandBuilder: "Constructor de Marca con IA",
    strategyComplete: "¡Estrategia Completa!",
    strategyCompleteDesc:
      "Tu voz de marca, personalidad y guías de contenido han sido guardadas.",
    continueToVisualIdentity: "¿Continuar a Identidad Visual?",
    continueToVisualIdentityDesc:
      "Diseña tu paleta de colores, tipografía y dirección de arte basándose en la estrategia que acabamos de crear.",
    doneForNow: "Listo por ahora",
    continueToPhase2: "Continuar a Fase 2",
    strategySavedToast: "¡Estrategia guardada!",
    strategyFailedToast: "Error al guardar la estrategia. Inténtalo de nuevo.",
    visualIdentitySaved: "¡Identidad visual guardada!",
    visualIdentityFailed: "Error al guardar la identidad visual. Inténtalo de nuevo.",

    // PhaseSelector
    phaseSelectorTitle: "Constructor de Marca con IA",
    phaseSelectorDesc:
      "Construye tu identidad de marca completa en dos fases. Comienza con la estrategia, luego diseña tus visuales.",
    phase1Title: "Fase 1: Estrategia y Núcleo",
    phase1Desc: "Define la voz, posicionamiento y panorama de mercado de tu marca",
    complete: "Completo",
    recommendedToStart: "Recomendado para comenzar",
    phase1LongDesc:
      "Responde preguntas sobre tu negocio y proporciona ejemplos de contenido. La IA generará tu estrategia de marca, investigacion de mercado, personalidad y guías de contenido.",
    phase1BrandEssence: "Esencia de Marca",
    phase1Positioning: "Posicionamiento",
    phase1Personality: "Personalidad",
    phase1VoiceTone: "Voz y Tono",
    phase1ContentRules: "Reglas de Contenido",
    phase1MarketResearch: "Investigacion de Mercado",
    textBasedInputs: "Entradas de texto",
    startPhase1: "Iniciar Fase 1",
    regenerate: "Regenerar",
    phase2Title: "Fase 2: Identidad Visual",
    phase2Desc: "Diseña tu sistema visual de marca",
    locked: "Bloqueado",
    phase2LongDesc:
      "Selecciona preferencias visuales e inspiraciones. La IA generará tu paleta de colores, tipografía y dirección de arte basándose en tu estrategia.",
    phase2Colors: "Colores",
    phase2Typography: "Tipografía",
    phase2ArtDirection: "Dirección de Arte",
    phase2Photography: "Fotografía",
    phase2Iconography: "Iconografía",
    visualPickers: "Selectores visuales",
    startPhase2: "Iniciar Fase 2",
    completePhase1ToUnlock: "Completa la Fase 1 para desbloquear",
    howItWorks: "Cómo funciona",
    howItWorksDesc:
      "La Fase 1 establece **quién es tu marca** a través de preguntas de texto. La Fase 2 usa esa base para crear **cómo se ve tu marca** a través de preferencias visuales. Completa ambas para una identidad de marca integral.",

    // Phase1
    generatingStrategy: "Generando Tu Estrategia de Marca",
    analyzingCompany: "Analizando información de la empresa...",
    understandingAudience: "Comprendiendo tu audiencia...",
    examiningContent: "Examinando ejemplos de contenido...",
    identifyingPersonality: "Identificando personalidad de marca...",
    craftingPositioning: "Elaborando declaración de posicionamiento...",
    buildingContentGuidelines: "Construyendo guías de contenido...",
    finalizingStrategy: "Finalizando estrategia...",
    failedToGenerateStrategy: "Error al generar la estrategia. Inténtalo de nuevo.",
    percentCompleteLabel: "% completo",

    // Phase1Input
    phase1InputTitle: "Fase 1: Estrategia y Núcleo",
    phase1InputDesc: "Cuéntanos sobre tu negocio y cómo te comunicas",
    companyName: "Nombre de la Empresa",
    companyNamePlaceholder: "ej., SOLARIIS",
    industry: "Industria",
    selectIndustry: "Selecciona tu industria",
    specifyIndustry: "Especifica tu industria...",
    whatDoesCompanyDo: "¿Qué hace tu empresa?",
    whatDoesCompanyDoPlaceholder:
      "Describe tus productos/servicios, qué te hace único y el valor que proporcionas...",
    idealCustomer: "¿Quién es tu cliente ideal?",
    idealCustomerPlaceholder:
      "Describe tu audiencia objetivo: demografía, necesidades, puntos de dolor, objetivos...",
    threeWords: "3 palabras que describen tu marca",
    optional: "(opcional)",
    threeWordsHelp: "Ayuda a guiar la IA con adjetivos de personalidad",
    threeWordsPlaceholder: "ej., Audaz, Innovador, Confiable...",
    keyCompetitors: "Competidores clave",
    keyCompetitorsHelp: "Ayúdanos a entender tu panorama competitivo",
    keyCompetitorsPlaceholder: "ej., Competidor A, Competidor B...",
    whatMakesDifferent: "¿Qué te hace diferente?",
    whatMakesDifferentPlaceholder:
      "Tu propuesta de valor única - qué te diferencia de los competidores...",
    contentExamples: "Ejemplos de Contenido",
    contentExamplesDesc:
      "Comparte 3-10 piezas de contenido que representen la voz de tu marca (subtítulos, titulares, copy)",
    enhanceWithAi: "Mejorar con IA",
    enhance: "Mejorar",
    exampleContent: "Contenido de ejemplo",
    addAnotherExample: "Agregar otro ejemplo...",
    add: "Agregar",
    ofMinExamples: "de 3+ ejemplos",
    minimumRequired: "(mínimo 3 requeridos)",
    back: "Atrás",
    generateStrategy: "Generar Estrategia",
    generating: "Generando...",
    fillRequiredFields: "Por favor completa todos los campos requeridos",
    enterDescriptionFirst: "Por favor ingresa una descripción primero",
    descriptionEnhanced: "¡Descripción mejorada!",
    enhancementFailed: "Error al mejorar",
    enterContentFirst: "Por favor ingresa contenido primero",
    contentEnhanced: "¡Contenido mejorado!",
    failedToEnhanceDescription: "Error al mejorar la descripción",
    failedToEnhanceContent: "Error al mejorar el contenido",

    // Phase1Review
    reviewStrategyCore: "Revisar Estrategia y Núcleo",
    reviewStrategyDesc: "Revisa cada sugerencia y acepta, edita o salta para conservar los datos existentes.",
    reviewed: "revisado",
    reviewBrandStrategy: "Estrategia de Marca",
    reviewBrandEssence: "Esencia de Marca",
    reviewBrandEssenceDesc: "Propósito, misión, visión y valores fundamentales",
    reviewPositioning: "Declaración de Posicionamiento",
    reviewPositioningDesc: "Cómo tu marca se diferencia en el mercado",
    reviewBrandPillars: "Pilares de Marca",
    reviewBrandPillarsDesc: "Temas clave y áreas de enfoque para tu marca",
    reviewBrandPromise: "Promesa de Marca",
    reviewBrandPromiseDesc: "Lo que tu marca se compromete a entregar",
    reviewBrandArchetype: "Arquetipo de Marca",
    reviewBrandArchetypeDesc: "El arquetipo junguiano que mejor representa la personalidad de la marca",
    reviewMarketContextLabel: "Contexto de Mercado",
    reviewMarketContextDesc: "Categoria de mercado, competidores clave y oportunidades de espacio blanco",
    reviewCompetitiveAdvantages: "Ventajas Competitivas",
    reviewCompetitiveAdvantagesDesc: "Ventajas clave que diferencian a la marca de sus competidores",
    reviewEmotionalBenefits: "Beneficios Emocionales",
    reviewEmotionalBenefitsDesc: "Como la marca hace sentir a los clientes",
    reviewFunctionalBenefits: "Beneficios Funcionales",
    reviewFunctionalBenefitsDesc: "Beneficios tangibles y valor que entrega la marca",
    reviewToneOfLeadership: "Tono de Liderazgo",
    reviewToneOfLeadershipDesc: "Como la marca lidera y comunica autoridad en su espacio",
    reviewBrandNarrative: "Narrativa de Marca",
    reviewBrandNarrativeDesc: "La historia general que conecta la marca con su audiencia",
    reviewPersonalityVoice: "Personalidad y Voz",
    reviewBrandVoice: "Voz de Marca",
    reviewBrandVoiceDesc: "Estilo general de comunicación y tono",
    reviewPersonalityTraits: "Rasgos de Personalidad",
    reviewPersonalityTraitsDesc: "Adjetivos que describen el carácter de tu marca",
    reviewTargetAudience: "Audiencia Objetivo",
    reviewTargetAudienceDesc: "Tu perfil de cliente ideal",
    reviewVoiceToneRules: "Reglas de Voz y Tono",
    reviewVoiceToneRulesDesc: "Estilo de escritura, punto de vista y formalidad",
    reviewContentGuidelines: "Guías de Contenido",
    reviewContentDos: "Qué Hacer",
    reviewContentDosDesc: "Cosas para incluir en tu contenido",
    reviewContentDonts: "Qué No Hacer",
    reviewContentDontsDesc: "Cosas a evitar en tu contenido",
    reviewSuggestedHashtags: "Hashtags Sugeridos",
    reviewSuggestedHashtagsDesc: "Hashtags predeterminados para tu contenido",
    reviewMarketResearch: "Investigacion de Mercado",
    reviewMarketResearchDesc: "Vision general del mercado, panorama competitivo y analisis FODA",
    backToInput: "Volver a Entrada",
    saveContinue: "Guardar y Continuar",

    // Phase2
    designingVisualIdentity: "Diseñando Tu Identidad Visual",
    analyzingVisualPreferences: "Analizando preferencias visuales...",
    selectingColorPalette: "Seleccionando paleta de colores...",
    pairingTypography: "Combinando tipografía...",
    definingArtDirection: "Definiendo dirección de arte...",
    settingPhotographyGuidelines: "Estableciendo guías de fotografía...",
    establishingIconography: "Estableciendo iconografía...",
    finalizingVisualIdentity: "Finalizando identidad visual...",
    failedToGenerateVisual: "Error al generar la identidad visual. Inténtalo de nuevo.",

    // Phase2Input
    phase2InputTitle: "Fase 2: Identidad Visual",
    phase2InputDesc: "Selecciona tus preferencias visuales - diseñaremos tu apariencia",
    analyzeUpload: "Analizar Archivo",
    analyzeUploadDesc: "Sube un archivo de referencia para completar preferencias automáticamente",
    analyzeUploadAccepts: "PDF, PNG, JPG o SVG (máx 5 MB)",
    analyzeUploadAnalyzing: "Extrayendo preferencias visuales...",
    analyzeUploadComplete: "{count} campos completados",
    analyzeUploadFailed: "Error al analizar el archivo. Prueba con otro formato o archivo.",
    analyzeUploadClear: "Limpiar",
    usingStrategyFrom: "Usando estrategia de la Fase 1:",
    visualSuggestionsAligned: "Las sugerencias visuales se alinearán con la personalidad de tu marca:",
    colorMood: "Estado de Ánimo del Color",
    selectUpTo2: "(selecciona hasta 2)",
    colorMoodDesc: "¿Qué sensación deben evocar tus colores?",
    existingBrandColors: "Colores de Marca Existentes",
    addColorsToKeep: "Agrega colores que quieras mantener o sobre los cuales construir",
    hexcodePlaceholder: "#HEXCODE",
    typographyFeel: "Sensación Tipográfica",
    selectOne: "(selecciona uno)",
    typographyFeelDesc: "¿Qué personalidad debe tener tu tipografía?",
    photographyStyle: "Estilo Fotográfico",
    selectUpTo3: "(selecciona hasta 3)",
    photographyStyleDesc: "¿Qué tipo de imágenes representan tu marca?",
    visualInspiration: "Inspiración Visual",
    visualInspirationDesc: "Marcas cuyo estilo visual admiras",
    whereWillBrandAppear: "¿Dónde aparecerá tu marca?",
    selectAllApply: "Selecciona todos los que apliquen - ayuda a optimizar las elecciones visuales",
    visualKeywords: "Palabras Clave Visuales",
    visualKeywordsDesc: "Palabras que describen la estética deseada",
    visualKeywordsPlaceholder: "ej., Minimalista, Lujoso, Orgánico...",
    describeYourLogo: "Describe Tu Logo",
    describeYourLogoPlaceholder:
      "Describe tu logo si tienes uno (estilo, colores, forma) - nos ayuda a sugerir visuales complementarios...",
    generateVisualIdentity: "Generar Identidad Visual",

    // Phase2Review
    reviewVisualIdentity: "Revisar Identidad Visual",
    reviewVisualDesc: "Revisa cada sugerencia visual y acepta, edita o salta.",
    colorSystem: "Sistema de Color",
    colorPalette: "Paleta de Colores",
    colorPaletteDesc: "Colores primarios y secundarios de marca",
    reviewTypography: "Tipografía",
    fontSelection: "Selección de Fuentes",
    fontSelectionDesc: "Fuentes de título, cuerpo y monoespaciada",
    reviewArtDirection: "Dirección de Arte",
    reviewVisualStyleNotes: "Notas de Estilo Visual",
    reviewVisualStyleNotesDesc: "Dirección estética general",
    reviewImageryStyle: "Estilo de Imágenes",
    reviewImageryStyleDesc: "Estándares de fotografía y visuales",
    reviewPhotography: "Fotografía",
    photoStyle: "Estilo Fotográfico",
    photoStyleDesc: "Iluminación, tono y ambiente",
    reviewIconography: "Iconografía",
    iconStyle: "Estilo de Iconos",
    iconStyleDesc: "Peso de trazo y guías de estilo",
    saveFinish: "Guardar y Finalizar",
    primary: "Primario",
    secondary: "Secundario",
    heading: "Título",
    body: "Cuerpo",
    monospace: "Monoespaciado",
    photographyStandards: "Estándares de Fotografía",
    composition: "Composición",
    subjectMatter: "Tema",
    lighting: "Iluminación",
    tone: "Tono",
    saturation: "Saturación",
    emotion: "Emoción",
    stroke: "Trazo",
    corners: "Esquinas",
    style: "Estilo",
    headingFontLabel: "Fuente de Título",
    headingWeightLabel: "Peso de Título",
    bodyFontLabel: "Fuente de Cuerpo",
    bodyWeightLabel: "Peso de Cuerpo",
    monospaceFontLabel: "Fuente Monoespaciada",
    addPrimary: "Agregar primario",
    addSecondary: "Agregar secundario",
    colorNameLabel: "Nombre del color",

    // FieldReviewer
    existing: "Existente",
    accepted: "Aceptado",
    edited: "Editado",
    skipped: "Omitido",
    skip: "Omitir",
    accept: "Aceptar",
    useNew: "Usar Nuevo",
    addNewItem: "Agregar nuevo elemento...",
    fieldPurpose: "Proposito",
    fieldMission: "Mision",
    fieldVision: "Vision",
    fieldValues: "Valores",
    addValue: "Agregar valor...",
    fieldWritingStyle: "Estilo de Escritura",
    fieldPointOfView: "Punto de Vista",
    fieldFormality: "Formalidad",
    fieldFormal: "Formal",
    fieldNeutral: "Neutral",
    fieldInformal: "Informal",

    // MarketResearchView — Section Group Headers
    mrGroupOverview: "Vision General y Dimensionamiento",
    mrGroupOverviewDesc: "Panorama del mercado, tamano y proyecciones de crecimiento",
    mrGroupCompetitive: "Panorama Competitivo",
    mrGroupCompetitiveDesc: "Competidores, barreras de mercado y posicionamiento",
    mrGroupInternal: "Evaluacion Interna",
    mrGroupInternalDesc: "Analisis FODA y evaluacion de factibilidad",
    mrGroupRegulatory: "Regulatorio y Fuentes",
    mrGroupRegulatoryDesc: "Infraestructura digital, notas regulatorias, fuentes y metodologia",

    // MarketResearchView — Existing Field Labels
    mrOverview: "Vision General del Mercado",
    mrOverviewDesc: "Tendencias clave y dinamicas del mercado",
    mrOverviewPlaceholder: "Describe el panorama general del mercado, tendencias clave y dinamicas...",
    mrSizing: "Dimensionamiento del Mercado",
    mrSizingDesc: "Tamano del mercado y potencial de crecimiento",
    mrSizingPlaceholder: "Incluye TAM (Mercado Total Direccionable), SAM (Mercado Direccionable Servible), SOM (Mercado Obtenible Servible) y estimaciones de ingresos relevantes...",
    mrCompetitiveLandscape: "Panorama Competitivo",
    mrCompetitiveLandscapeDesc: "Competidores principales y posicionamiento",
    mrCompetitiveLandscapePlaceholder: "Describe el panorama competitivo, los principales actores y estrategias de posicionamiento...",
    mrDigitalInfra: "Infraestructura Digital",
    mrDigitalInfraDesc: "Presencia digital y canales tecnologicos",
    mrDigitalInfraPlaceholder: "Describe la infraestructura digital, presencia en linea y canales tecnologicos...",
    mrSwotAnalysis: "Analisis FODA",
    mrSwotAnalysisDesc: "Fortalezas, debilidades, oportunidades y amenazas",
    mrFeasibility: "Evaluacion de Factibilidad",
    mrFeasibilityDesc: "Viabilidad del proyecto y factores criticos",
    mrFeasibilityPlaceholder: "Evalua la factibilidad: viabilidad, factores criticos, requerimientos de recursos, mitigacion de riesgos...",
    mrRegulatory: "Notas Regulatorias",
    mrRegulatoryDesc: "Consideraciones legales y normativas relevantes",
    mrRegulatoryPlaceholder: "Lista los requisitos regulatorios, consideraciones de cumplimiento y restricciones legales...",
    mrSources: "Fuentes",
    mrSourcesDesc: "Fuentes de investigacion y referencias",

    // MarketResearchView — New Field Labels
    mrKeyCompetitors: "Competidores Clave",
    mrKeyCompetitorsDesc: "Perfiles detallados de competidores con fortalezas, debilidades y participacion de mercado",
    mrAddCompetitor: "Agregar Competidor",
    mrRemoveCompetitor: "Eliminar",
    mrCompetitorName: "Nombre del Competidor",
    mrCompetitorNamePlaceholder: "ej., Competidor A",
    mrCompetitorStrengths: "Fortalezas",
    mrCompetitorStrengthsPlaceholder: "Fortalezas clave de este competidor...",
    mrCompetitorWeaknesses: "Debilidades",
    mrCompetitorWeaknessesPlaceholder: "Debilidades o brechas clave...",
    mrCompetitorMarketShare: "Participacion de Mercado",
    mrCompetitorMarketSharePlaceholder: "ej., ~15% del mercado",
    mrMarketGrowth: "Crecimiento del Mercado",
    mrMarketGrowthDesc: "CAGR, proyecciones de crecimiento y etapa de madurez",
    mrMarketGrowthPlaceholder: "Describe la tasa de crecimiento del mercado (CAGR), proyecciones de crecimiento y etapa de madurez del mercado...",
    mrMarketBarriers: "Barreras del Mercado",
    mrMarketBarriersDesc: "Barreras de entrada, requisitos de capital y costos de cambio",
    mrMarketBarriersPlaceholder: "Describe las barreras de entrada, requisitos de capital, costos de cambio y obstaculos regulatorios...",
    mrResearchMethodology: "Metodologia de Investigacion",
    mrResearchMethodologyDesc: "Fuentes de datos, notas metodologicas y niveles de confianza",
    mrResearchMethodologyPlaceholder: "Describe las fuentes de datos, metodos de investigacion, tamanos de muestra, niveles de confianza y limitaciones...",

    // MarketResearchView — SWOT Labels
    mrStrengths: "Fortalezas",
    mrWeaknesses: "Debilidades",
    mrOpportunities: "Oportunidades",
    mrThreats: "Amenazas",

    // MarketResearchView — Edit Mode
    mrEditMode: "Editar",
    mrSaveChanges: "Guardar",
    mrCancelEdit: "Cancelar",
    mrGeneratedAt: "Generado",
    mrNoData: "Sin definir",
    mrNoPending: "Investigacion de mercado pendiente - el agente aun no ha completado este paso.",
    mrSaved: "Investigacion guardada",
    mrSaveFailed: "Error al guardar",
    mrAddSource: "Agregar fuente...",
    mrAddSwotItem: "Agregar...",
    mrNoCompetitors: "Sin competidores perfilados.",

    // LayoutEditor — Section Group Headers
    layoutSpacingRadii: "Espaciado y Radios",
    layoutSpacingRadiiDesc: "Escala de tokens de espaciado y valores de radio de borde",
    layoutGridSystem: "Sistema de Grilla",
    layoutGridSystemDesc: "Especificaciones de columnas para escritorio, tableta y movil",
    layoutMarginsTemplates: "Margenes, Relleno y Plantillas",
    layoutMarginsTemplatesDesc: "Margenes a nivel de pagina, preajustes de relleno y referencias de plantillas",

    // LayoutEditor — Spacing & Radii Descriptions
    spacingTokensTitle: "Tokens de Espaciado",
    spacingTokensDesc: "Escala base de espaciado (xs a 2xl) para espacios en blanco consistentes",
    borderRadiiTitle: "Radios de Borde",
    borderRadiiDesc: "Tokens de radio de esquina para elementos UI (sm, md, lg, full)",

    // LayoutEditor — Grid System
    gridDesktop: "Escritorio",
    gridTablet: "Tableta",
    gridMobile: "Movil",
    gridColumns: "Columnas",
    gridGutter: "Canaleta",
    gridMargin: "Margen",
    gridColumnsPlaceholder: "ej. 12",
    gridGutterPlaceholder: "ej. 24px",
    gridMarginPlaceholder: "ej. 32px",
    gridEditSpecs: "Editar",
    gridSaveSpecs: "Guardar",
    gridCancelEdit: "Cancelar",
    gridNoSpecs: "Sin especificaciones de grilla. Haz clic en Editar para configurar tu sistema de grilla.",
    gridPreview: "Vista previa",

    // LayoutEditor — Margins & Padding
    marginsAndPadding: "Margenes y Relleno",
    marginsAndPaddingDesc: "Contextos de espaciado comunes para nivel de pagina y componentes",
    marginKey: "Margen",
    paddingKey: "Relleno",
    marginValue: "Valor",
    paddingValue: "Valor",
    addMarginKey: "Agregar clave de margen...",
    addPaddingKey: "Agregar clave de relleno...",
    removeKey: "Eliminar",
    marginPresets: "Margenes",
    paddingPresets: "Relleno",
    noMarginsOrPadding: "Sin margenes o relleno definidos.",

    // LayoutEditor — Layout Templates
    layoutTemplates: "Plantillas de Layout",
    layoutTemplatesDesc: "Patrones de layout reutilizables y referencias de plantillas",
    addTemplate: "Agregar plantilla",
    removeTemplate: "Eliminar",
    templatePlaceholder: "ej. Hero + grilla de 3 columnas",
    noTemplates: "Sin plantillas de layout definidas.",

    // LayoutEditor — Token Labels
    spacingScale: "Escala de Espaciado",
    radiiScale: "Escala de Radios",
    pxEquivalent: "equivalente en px",

    // LayoutEditor — Save/status
    layoutSaved: "Configuracion de layout guardada",
    layoutSaveFailed: "Error al guardar la configuracion de layout",

    // AIContextViewer — Enhanced Section Headers
    aiContextOverview: "Vision General del Contexto",
    aiContextOverviewDesc: "Estadisticas de completitud y que es el Contexto IA",
    aiContextStrategyPersonality: "Contexto de Estrategia y Personalidad",
    aiContextStrategyPersonalityDesc: "Datos compilados de estrategia y personalidad para consumo de IA",
    aiContextVisualIdentity: "Contexto de Identidad Visual",
    aiContextVisualIdentityDesc: "Colores, tipografia y direccion de arte compilados para IA",
    aiContextMarketResearch: "Contexto de Mercado e Investigacion",
    aiContextMarketResearchDesc: "Vision general del mercado, panorama competitivo y tendencias clave",
    aiContextJsonExport: "Exportacion JSON Completa",
    aiContextJsonExportDesc: "Formato JSON crudo para uso directo en agentes IA y herramientas MCP",

    // AIContextViewer — Field Labels
    aiNotSet: "No configurado",
    aiSetInTab: "Configurar en pestana {tab}",
    aiFieldPurpose: "Proposito",
    aiFieldMission: "Mision",
    aiFieldVision: "Vision",
    aiFieldValues: "Valores",
    aiFieldPositioning: "Posicionamiento",
    aiFieldPromise: "Promesa",
    aiFieldArchetype: "Arquetipo",
    aiFieldPillars: "Pilares",
    aiFieldTargetAudience: "Audiencia Objetivo",
    aiFieldTargetSegments: "Segmentos Objetivo",
    aiFieldTraits: "Rasgos de Personalidad",
    aiFieldVoice: "Resumen de Voz",
    aiFieldWritingStyle: "Estilo de Escritura",
    aiFieldPointOfView: "Punto de Vista",
    aiFieldFormality: "Formalidad",
    aiFieldToneDimensions: "Dimensiones del Tono",
    aiFieldChannelVoice: "Voz por Canal",
    aiFieldDos: "Que Hacer",
    aiFieldDonts: "Que No Hacer",
    aiFieldHashtags: "Hashtags",
    aiFieldPrimaryColors: "Colores Primarios",
    aiFieldSecondaryColors: "Colores Secundarios",
    aiFieldFunctionalColors: "Colores Funcionales",
    aiFieldHeadingFont: "Fuente de Titulo",
    aiFieldBodyFont: "Fuente de Cuerpo",
    aiFieldMonoFont: "Fuente Mono",
    aiFieldWebFallbacks: "Fuentes de Respaldo Web",
    aiFieldVisualStyle: "Estilo Visual",
    aiFieldImageryStyle: "Estilo de Imagenes",
    aiFieldPhotoStyle: "Estilo Fotografico",
    aiFieldIconsStyle: "Estilo de Iconos",
    aiFieldMarketOverview: "Vision General del Mercado",
    aiFieldCompetitiveLandscape: "Panorama Competitivo",
    aiFieldKeyTrends: "Tendencias Clave",

    // AIContextViewer — Section Status Labels (new sections)
    contextArtDirection: "Direccion de Arte",
    contextMarketResearch: "Investigacion de Mercado",

    // AIContextViewer — Completion
    aiContextComplete: "Completo",
    aiContextIncomplete: "Incompleto",
    aiSectionsTracked: "secciones rastreadas",

    // Naming Phase
    namingPhaseTitle: "Nombre de Marca",
    namingPhaseDesc: "Generacion de nombres con IA y verificacion de dominios",
    namingPhaseLongDesc: "Genera nombres creativos de marca con analisis fonetico, revision cultural y verificacion de disponibilidad de dominios en tiempo real.",
    namingBadgeNameIdeas: "Ideas de Nombre",
    namingBadgeDomainCheck: "Verificar Dominio",
    namingBadgeCulturalFit: "Ajuste Cultural",
    namingBadgeTrademarkScreen: "Revision de Marca",
    namingStartNaming: "Comenzar Nombrado",
    namingRename: "Renombrar",
    namingRecommendedForNew: "Recomendado para marcas nuevas",
    namingCompleteResearchFirst: "Completa la Investigacion de Mercado primero",
    namingConfigurePrefs: "Configura preferencias para la generacion de nombres con IA",
    namingTargetAudience: "Audiencia Objetivo",
    namingStyles: "Estilos de Nombres",
    namingStylesDesc: "Selecciona los tipos de nombres que deseas generar",
    namingKeywordsInclude: "Palabras Clave a Incluir",
    namingKeywordsIncludeDesc: "Palabras o conceptos que quieres reflejados en el nombre",
    namingKeywordsAvoid: "Palabras Clave a Evitar",
    namingLanguageMarkets: "Mercados de Idioma",
    namingLanguageMarketsDesc: "Los nombres se verificaran en pronunciacion y significado en estos idiomas",
    namingMaxSyllables: "Silabas Maximas",
    namingDomainTlds: "Preferencias de TLD de Dominio",
    namingDomainTldsDesc: "Verificaremos la disponibilidad de estos dominios",
    namingYourIdeas: "Tus Ideas de Nombre",
    namingYourIdeasDesc: "Evaluaremos estas junto con las sugerencias de IA",
    namingGenerateNames: "Generar Nombres",
    namingGenerating: "Generando Nombres de Marca...",
    namingAnalyzingMarket: "Analizando contexto de mercado...",
    namingGeneratingCandidates: "Generando candidatos de nombre...",
    namingExploringStyles: "Explorando estilos de nombres...",
    namingCheckingDomains: "Verificando disponibilidad de dominios...",
    namingScoringCandidates: "Puntuando candidatos...",
    namingRankingResults: "Clasificando resultados...",
    namingNamesGenerated: "Nombres generados!",
    namingCandidatesTitle: "Candidatos de Nombre",
    namingCandidatesDesc: "Clasificados por puntuacion compuesta. Haz clic en una tarjeta para ver detalles, luego selecciona tu nombre de marca.",
    namingYourIdea: "Tu idea",
    namingScore: "Puntuacion",
    namingSelect: "Seleccionar",
    namingSelected: "Seleccionado",
    namingPhonetic: "Fonetica",
    namingMemorability: "Memorabilidad",
    namingIndustryFit: "Ajuste Industrial",
    namingDifferentiation: "Diferenciacion",
    namingPronunciation: "Pronunciacion",
    namingCulturalNotes: "Notas Culturales",
    namingDomainAvailability: "Disponibilidad de Dominio",
    namingAvailable: "Disponible",
    namingTaken: "Ocupado",
    namingTrademarkLow: "Riesgo Bajo",
    namingTrademarkMedium: "Riesgo Medio",
    namingTrademarkHigh: "Riesgo Alto",
    namingConfirmName: "Confirmar Nombre",
    namingConfirmQuestion: "Usar este como tu nombre de marca?",
    namingConfirmDesc: "Este se usara para tu estrategia de marca en la siguiente fase.",
    namingRegenerate: "Regenerar",
    namingFromNamingPhase: "De la Fase de Nombre",
    namingBrandNameSelected: "Nombre de marca seleccionado!",
    namingFailedToSave: "Error al guardar los resultados de nombres",

    // Product Pipeline — PRD
    prdTitle: "Documento de Requisitos del Producto",
    prdGenerateButton: "Generar PRD",
    prdGenerateDescription: "Genera un PRD completo a partir del contexto de marca y las respuestas de intake del cliente.",
    prdRegenerate: "Regenerar",
    prdSave: "Guardar PRD",
    prdSaving: "Guardando...",
    prdFeatures: "Funcionalidades",
    prdProblemStatement: "Problema",
    prdTargetUsers: "Usuarios Objetivo",
    prdInfoArchitecture: "Arquitectura de Informacion",
    prdTechRequirements: "Requisitos Tecnicos",
    prdDesignRequirements: "Requisitos de Diseno",
    prdTimeline: "Cronograma",
    prdBudget: "Evaluacion de Presupuesto",
    prdSuccessMetrics: "Metricas de Exito",
    prdRisks: "Riesgos",
    prdNextSteps: "Proximos Pasos",

    // Product Pipeline — POC
    pocTitle: "Prueba de Concepto",
    pocDesignBuild: "Diseno + Build",
    pocDesignBuildDesc: "Genera disenos visuales primero con Stitch, luego construye el scaffold.",
    pocQuickBuild: "Build Rapido",
    pocQuickBuildDesc: "Genera un scaffold de codigo Next.js y despliega directamente.",
    pocComingSoon: "Proximamente",
    pocStartScaffold: "Iniciar Scaffold",
    pocStarting: "Iniciando...",
    pocGenerating: "Generando scaffold...",
    pocViewPreview: "Ver Preview",
    pocViewRepo: "Ver Repositorio",

    // Unified Image Creator
    imageCreatorTitle: "Creador de Imagenes",
    imageCreatorContext: "Contexto",
    imageCreatorStyle: "Estilo",
    imageCreatorCustomStyle: "Estilo Personalizado",
    imageCreatorCustomStylePlaceholder: "Describe tu estilo visual personalizado...",
    imageCreatorAspectRatio: "Proporcion",
    imageCreatorPrompt: "Prompt",
    imageCreatorPromptPlaceholder: "Describe la imagen que deseas generar...",
    imageCreatorPromptEnhanced: "Prompt mejorado con IA",
    imageCreatorEnhance: "Mejorar Prompt",
    imageCreatorGenerate: "Generar",
    imageCreatorGenerating: "Generando imagen...",
    imageCreatorSaveToMoodboard: "Guardar en Moodboard",
    imageCreatorSaved: "Guardado en Moodboard",
    imageCreatorUseImage: "Usar Imagen",
    imageCreatorBrandContext: "Marca",
    imageCreatorEmptyPreview: "Tu imagen generada aparecerá aquí",
    imageCreatorViewFullSize: "Ver a pantalla completa",
    contextMoodboard: "Moodboard",
    contextSocialPost: "Post Social",
    contextBrandTemplate: "Plantilla de Marca",
    styleClean: "Limpio",
    styleVibrant: "Vibrante",
    styleMinimal: "Minimal",
    styleBold: "Audaz",
    styleCustom: "Personalizado",
  },
};
